import { useState, useEffect, useMemo } from "react";
import apiFetch from "../../apiFetch.js";
import { T, THEMES, REL } from "../../theme.js";
import { usePeople } from "../../context/PeopleContext.jsx";
import { supabase } from "../../supabaseClient.js";
import { Tog, Card, CardHead, Row } from "../ui/index.jsx";
import { isNative, openNativeOAuth, openOAuthPopup, fetchOAuthUrl } from "../../utils/nativeOAuth.js";
import { cleanupPushToken } from "../../utils/pushNotifications.js";
import { hasCameraPlugin, pickProfilePhoto, uploadFileToStorage } from "../../utils/camera.js";
import { PL, AgreementsTab } from "../agreements/index.jsx";
import { getDisplayName, saveDisplayName } from "../../utils/displayName.js";
import { resizeFileToDataUrl } from "../../utils/helpers.js";

function AppearanceCard(){
  const {compact,setCompact,themeKey,setThemeKey,setCustomTheme}=usePeople();
  const [customColor,setCustomColor]=useState(()=>{try{return localStorage.getItem("orbit_custom_color")||"#9B52C8";}catch{return"#9B52C8";}});
  const hexToRgb=h=>{const r=parseInt(h.slice(1,3),16),g=parseInt(h.slice(3,5),16),b=parseInt(h.slice(5,7),16);return[r,g,b];};
  const rgbToHex=(r,g,b)=>"#"+[r,g,b].map(v=>Math.max(0,Math.min(255,Math.round(v))).toString(16).padStart(2,"0")).join("");
  const mix=(hex,white,t)=>{const[r,g,b]=hexToRgb(hex);return rgbToHex(r+(255-r)*t*white,g+(255-g)*t*white,b+(255-b)*t*white);};
  const darken=(hex,t)=>{const[r,g,b]=hexToRgb(hex);return rgbToHex(r*(1-t),g*(1-t),b*(1-t));};
  const buildCustomTheme=(accent)=>({
    name:"Custom",
    bg:mix(accent,1,0.94),surface:"#FFFFFF",panel:mix(accent,1,0.97),
    mist:mix(accent,1,0.90),edge:mix(accent,1,0.82),edgeMid:mix(accent,1,0.70),
    muted:mix(accent,0,0.55),mid:darken(accent,0.25),bark:darken(accent,0.45),ink:darken(accent,0.65),
    clay:accent,partner:accent,family:"#5E8C65",friend:"#4A8FA8",coparent:mix(accent,0,0.3),
  });
  const applyCustom=(hex)=>{
    setCustomColor(hex);
    try{localStorage.setItem("orbit_custom_color",hex);}catch{}
    if(themeKey==="custom"){const ct=buildCustomTheme(hex);setCustomTheme(ct);try{localStorage.setItem("orbit_custom_theme",JSON.stringify(ct));}catch{}}
  };
  const selectCustom=()=>{const ct=buildCustomTheme(customColor);setCustomTheme(ct);setThemeKey("custom");try{localStorage.setItem("orbit_custom_theme",JSON.stringify(ct));}catch{}};
  const isCustom=themeKey==="custom";
  return(<Card>
    <CardHead icon="🎨" label="Appearance"/>
    <Row label="Compact density" sub="Tighter spacing throughout the app" right={<Tog on={compact} set={setCompact} c={T.bark}/>}/>
    <div style={{padding:"12px 16px"}}>
      <p style={{fontSize:12,fontWeight:500,color:T.ink,marginBottom:10}}>Color theme</p>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {Object.entries(THEMES).map(([key,th])=>(
          <button key={key} onClick={()=>setThemeKey(key)} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,padding:"8px 10px",borderRadius:11,border:`2px solid ${themeKey===key?th.clay:th.edgeMid}`,background:themeKey===key?th.clay+"18":th.bg,cursor:"pointer",transition:"all .15s",minWidth:62}}>
            <div style={{display:"flex",gap:3}}>
              {[th.partner,th.family,th.friend,th.coparent].map((c,i)=>(
                <div key={i} style={{width:10,height:10,borderRadius:"50%",background:c}}/>
              ))}
            </div>
            <div style={{width:36,height:16,borderRadius:5,background:th.surface,border:`1px solid ${th.edge}`,overflow:"hidden",display:"flex"}}>
              <div style={{width:"40%",background:th.panel}}/><div style={{width:"60%",background:th.surface}}/>
            </div>
            <span style={{fontSize:11,color:th.clay,fontWeight:themeKey===key?700:400,opacity:themeKey===key?1:0.7}}>{th.name}</span>
          </button>
        ))}
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,padding:"8px 10px",borderRadius:11,border:`2px solid ${isCustom?customColor:T.edge}`,background:isCustom?customColor+"18":T.mist,transition:"all .15s",minWidth:62,cursor:"pointer"}} onClick={selectCustom}>
          <div style={{position:"relative",width:28,height:28,borderRadius:"50%",background:"conic-gradient(#C17B4E,#5E8C65,#4A8FA8,#7B6BAE,#C4607A,#7B52C8,#C17B4E)",border:`2px solid ${isCustom?customColor:T.edge}`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
            <input type="color" value={customColor} onChange={e=>{e.stopPropagation();applyCustom(e.target.value);}} onClick={e=>e.stopPropagation()} style={{opacity:0,position:"absolute",inset:0,width:"100%",height:"100%",cursor:"pointer",border:"none",padding:0}}/>
            <span style={{fontSize:12,color:"white",fontWeight:700,pointerEvents:"none",textShadow:"0 1px 2px rgba(0,0,0,.4)"}}>+</span>
          </div>
          <span style={{fontSize:11,color:isCustom?customColor:T.muted,fontWeight:isCustom?700:400}}>Custom</span>
        </div>
      </div>
      {isCustom&&<div style={{marginTop:10,display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,background:customColor+"10",border:`1px solid ${customColor}30`}}>
        <div style={{width:24,height:24,borderRadius:6,background:customColor,flexShrink:0}}/>
        <div style={{flex:1}}>
          <p style={{fontSize:12,fontWeight:600,color:T.ink}}>Custom accent</p>
          <p style={{fontSize:11,color:T.muted}}>{customColor}</p>
        </div>
        <label style={{padding:"5px 10px",borderRadius:8,background:customColor,color:"white",fontSize:11,fontWeight:600,cursor:"pointer",border:"none",position:"relative"}}>
          Change
          <input type="color" value={customColor} onChange={e=>applyCustom(e.target.value)} style={{opacity:0,position:"absolute",width:0,height:0}}/>
        </label>
      </div>}
    </div>
  </Card>);
}

const NOTIF_GROUPS=[
  {label:"Scheduling",icon:"📅",keys:[
    {key:"schedule_received",   label:"New schedule requests"},
    {key:"schedule_confirmed",  label:"Confirmations"},
    {key:"schedule_declined",   label:"Declines"},
    {key:"counter_proposed",    label:"Counter-proposals"},
  ]},
  {label:"Connections",icon:"🔗",keys:[
    {key:"new_connection",      label:"New connection requests"},
    {key:"connection_drift",    label:"Connection drift nudges"},
  ]},
  {label:"Reminders",icon:"◷",keys:[
    {key:"birthday_reminder",   label:"Birthday reminders"},
    {key:"anniversary_reminder",label:"Anniversary reminders"},
    {key:"anchor_reminder",     label:"Anchor night reminders"},
  ]},
  {label:"Fairness",icon:"⚖️",keys:[
    {key:"fairness_drift",      label:"Fairness drift warnings"},
    {key:"fairness_summary",    label:"Monthly fairness summary"},
  ]},
];

function NotifChannelPrefs(){
  const {notifPrefs,setNotifPrefs}=usePeople();
  const [collapsed,setCollapsed]=useState(()=>new Set(NOTIF_GROUPS.map(g=>g.label)));
  const toggleCollapse=(key)=>setCollapsed(prev=>{const s=new Set(prev);s.has(key)?s.delete(key):s.add(key);return s;});
  const toggle=(key)=>setNotifPrefs(prev=>({...prev,[key]:{...prev[key],email:!prev[key]?.email}}));
  const isGroupAllOn=(group)=>group.keys.every(r=>notifPrefs[r.key]?.email!==false);
  const toggleGroupAll=(group)=>{
    const allOn=isGroupAllOn(group);
    setNotifPrefs(prev=>{
      const next={...prev};
      for(const r of group.keys) next[r.key]={...next[r.key],email:!allOn};
      return next;
    });
  };
  return(<>
    {NOTIF_GROUPS.map(group=>{const isCollapsed=collapsed.has(group.label);return(
      <Card key={group.label}>
        <div onClick={()=>toggleCollapse(group.label)} style={{padding:"10px 16px 8px",borderBottom:isCollapsed?'none':`1px solid ${T.edge}`,background:T.panel,display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none"}}>
          <span style={{fontSize:10,color:T.muted,transition:"transform .2s",transform:isCollapsed?"rotate(-90deg)":"rotate(0deg)"}}>▼</span>
          <span style={{fontSize:14}}>{group.icon}</span>
          <span style={{fontSize:13,fontWeight:700,color:T.bark,flex:1}}>{group.label}</span>
          <div onClick={e=>e.stopPropagation()}>
            <Tog on={isGroupAllOn(group)} set={()=>toggleGroupAll(group)} c={T.sage}/>
          </div>
        </div>
        {!isCollapsed&&group.keys.map((r,i)=>(
          <Row key={r.key} label={r.label} border={i<group.keys.length-1} right={
            <Tog on={notifPrefs[r.key]?.email!==false} set={()=>toggle(r.key)} c={T.sage}/>
          }/>
        ))}
      </Card>
    );})}
  </>);
}

const FAIRNESS_FREQ_OPTS = [
  {id:"never",    label:"Never",          sub:"Disable fairness drift alerts"},
  {id:"daily",    label:"Daily",          sub:"Notify every day while below 60%"},
  {id:"weekly",   label:"Weekly",         sub:"At most once per week (default)"},
  {id:"biweekly", label:"Every 2 weeks",  sub:"At most once every two weeks"},
  {id:"monthly",  label:"Monthly",        sub:"At most once per month"},
];

function FairnessFreqCard(){
  const {notifPrefs,setNotifPrefs}=usePeople();
  const freq=notifPrefs?.fairness_frequency||"weekly";
  return(<Card>
    <CardHead icon="⚖️" label="Fairness alert frequency"/>
    <div style={{padding:"4px 16px 12px"}}>
      <p style={{fontSize:12,color:T.muted,marginBottom:12,lineHeight:1.5}}>How often to notify you when fairness falls below 60%. Per-person overrides can be set in each person's Privacy tab.</p>
      {FAIRNESS_FREQ_OPTS.map(opt=>(
        <div key={opt.id} onClick={()=>setNotifPrefs(p=>({...p,fairness_frequency:opt.id}))} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:10,background:freq===opt.id?T.clay+"0C":T.surface,border:`1.5px solid ${freq===opt.id?T.clay+"50":T.edge}`,marginBottom:6,cursor:"pointer",transition:"all .14s"}}>
          <div style={{flex:1}}>
            <p style={{fontSize:13,fontWeight:freq===opt.id?600:400,color:freq===opt.id?T.ink:T.bark}}>{opt.label}</p>
            <p style={{fontSize:11,color:T.muted}}>{opt.sub}</p>
          </div>
          <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${freq===opt.id?T.clay:T.edgeMid}`,background:freq===opt.id?T.clay:"transparent",flexShrink:0}}/>
        </div>
      ))}
    </div>
  </Card>);
}

export function SettingsContent({onNav,isMobile,initialSection,googleOAuthError,outlookOAuthError,yahooOAuthError,caldavConnectError,onTakeTour}){
  const {people:PEOPLE,user,setUser,prefs,setPrefs,notifSettings,setNotifSettings,notifPrefs,setNotifPrefs,priv,setPriv,hideOffline,setHideOffline,blockNotes,setBlockNotes,privOverrides,setPrivOverrides}=usePeople();
  const [section,setSection]=useState(initialSection||"profile");

  // Profile — initialize directly from the logged-in user so "Morgan" never flashes
  const [editing,setEditing]=useState(false);
  const _initName=getDisplayName(user,"");
  const _initEmail=user?.email||"";
  const _initPhone=user?.user_metadata?.phone||"";
  const _initBirthday=user?.user_metadata?.birthday||"";
  const _initPhoto=user?.user_metadata?.photo||null;
  const _initFullName=(()=>{
    const m=user?.user_metadata||{};
    if(m.legal_full_name) return m.legal_full_name;
    const parts=[m.first_name||m.given_name,m.last_name||m.family_name].filter(Boolean);
    if(parts.length) return parts.join(" ");
    return m.full_name||m.name||"";
  })();
  const [profileName,setProfileName]=useState(_initName);
  const [profileEmail,setProfileEmail]=useState(_initEmail);
  const [profilePhone,setProfilePhone]=useState(_initPhone);
  const [profileBirthday,setProfileBirthday]=useState(_initBirthday);
  const [profilePhoto,setProfilePhoto]=useState(_initPhoto);
  const [profileFullName,setProfileFullName]=useState(_initFullName);
  const [profileDraft,setProfileDraft]=useState({name:_initName,email:_initEmail,phone:_initPhone,birthday:_initBirthday,photo:_initPhoto,fullName:_initFullName});
  const [showPwChange,setShowPwChange]=useState(false);
  const [pwDraft,setPwDraft]=useState({current:"",next:"",confirm:""});
  const [pwError,setPwError]=useState("");
  const [pwDone,setPwDone]=useState(false);
  // MFA state
  const [mfaFactors,setMfaFactors]=useState(null); // null=loading, []=none, [...]= enrolled
  const [mfaEnrolling,setMfaEnrolling]=useState(false);
  const [mfaQr,setMfaQr]=useState(null);
  const [mfaSecret,setMfaSecret]=useState(null);
  const [mfaFactorId,setMfaFactorId]=useState(null);
  const [mfaVerifyCode,setMfaVerifyCode]=useState("");
  const [mfaError,setMfaError]=useState("");
  const [mfaSuccess,setMfaSuccess]=useState("");
  // Invite link generation
  const [inviteRelType,setInviteRelType]=useState("partner");
  const [inviteLink,setInviteLink]=useState(null);
  const [inviteGenerating,setInviteGenerating]=useState(false);
  const [inviteGenError,setInviteGenError]=useState(null);
  const [inviteCopied,setInviteCopied]=useState(false);
  const generateInvite=async()=>{
    setInviteGenerating(true);
    setInviteGenError(null);
    try{
      const {data:{session}}=await supabase.auth.getSession();
      const res=await apiFetch("/api/invite/create",{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${session.access_token}`},
        body:JSON.stringify({relationship_type:inviteRelType}),
      });
      const json=await res.json();
      if(!res.ok) throw new Error(json.error||"Failed to create invite");
      setInviteLink(json.url);
    }catch(e){setInviteGenError(e.message);}
    finally{setInviteGenerating(false);}
  };
  const copyInviteLink=async()=>{
    try{await navigator.clipboard.writeText(inviteLink);setInviteCopied(true);setTimeout(()=>setInviteCopied(false),2000);}catch(_){}
  };

  useEffect(()=>{
    if(!user) return;
    const derivedName = getDisplayName(user,"You");
    const derivedEmail = user.email || profileEmail;
    const derivedPhone = user.user_metadata?.phone||profilePhone||"";
    const derivedBirthday = user.user_metadata?.birthday||profileBirthday||"";
    const derivedPhoto = user.user_metadata?.photo||profilePhoto||null;
    const _m=user.user_metadata||{};
    const _nameParts=[_m.first_name||_m.given_name,_m.last_name||_m.family_name].filter(Boolean);
    const derivedFullName = _m.legal_full_name||(_nameParts.length?_nameParts.join(" "):"")||_m.full_name||_m.name||profileFullName||"";
    setProfileName(derivedName);
    setProfileEmail(derivedEmail);
    setProfilePhone(derivedPhone);
    setProfileBirthday(derivedBirthday);
    setProfilePhoto(derivedPhoto);
    setProfileFullName(derivedFullName);
    setProfileDraft({name:derivedName,email:derivedEmail,phone:derivedPhone,birthday:derivedBirthday,photo:derivedPhoto,fullName:derivedFullName});
  },[user]);

  const handlePhotoSelect=(e)=>{
    const file=e.target.files?.[0];if(!file)return;
    // Try storage upload first; fall back to local data URL
    if(userId){
      uploadFileToStorage(file,userId)
        .then(url=>{if(url) setProfileDraft(d=>({...d,photo:url}));})
        .catch(()=>resizeFileToDataUrl(file).then(url=>{if(url) setProfileDraft(d=>({...d,photo:url}));}));
    } else {
      resizeFileToDataUrl(file).then(url=>{if(url) setProfileDraft(d=>({...d,photo:url}));});
    }
  };

  const handleProfileSave=async()=>{
    const newName=(profileDraft.name||profileName).trim()||profileName;
    const newEmail=(profileDraft.email||profileEmail).trim()||profileEmail;
    const newPhone=(profileDraft.phone||"").trim();
    const newBirthday=(profileDraft.birthday||"").trim();
    const newPhoto=profileDraft.photo||null;
    const newFullName=(profileDraft.fullName||"").trim();
    setProfileName(newName);
    setProfileEmail(newEmail);
    setProfilePhone(newPhone);
    setProfileBirthday(newBirthday);
    setProfilePhoto(newPhoto);
    setProfileFullName(newFullName);
    setEditing(false);
    if(!user) return;
    try{
      const updatePayload={};
      if(newName){
        saveDisplayName(newName, userId);
        updatePayload.data={...(user.user_metadata||{}),full_name:newName,name_explicitly_set:true,phone:newPhone,birthday:newBirthday,photo:newPhoto,legal_full_name:newFullName};
      } else {
        updatePayload.data={...(user.user_metadata||{}),phone:newPhone,birthday:newBirthday,photo:newPhoto,legal_full_name:newFullName};
      }
      if(newEmail!==user.email){
        updatePayload.email=newEmail;
      }
      if(Object.keys(updatePayload).length>0){
        const {data,error}=await supabase.auth.updateUser(updatePayload);
        if(!error&&data?.user){
          setUser(data.user);
          // Keep user_profiles in sync so other users get the latest info
          // when they add this user from the contact picker
          const uid=data.user.id;
          if(uid){
            supabase.from("user_profiles").upsert({
              user_id:uid,
              display_name:newName||null,
              legal_full_name:newFullName||null,
              email:newEmail||null,
              phone:newPhone||null,
              birthday:newBirthday?newBirthday:null,
              photo:newPhoto||null,
              updated_at:new Date().toISOString(),
            },{onConflict:"user_id"}).catch(e => console.error('[settings] user_profiles sync failed:', e));
          }
        }
      }
    }catch(e){
      // ignore for now; local state already updated
    }
  };

  const handlePasswordChange=async()=>{
    setPwError("");
    if(pwDraft.next.length<6){setPwError("Password must be at least 6 characters.");return;}
    if(pwDraft.next!==pwDraft.confirm){setPwError("Passwords don't match.");return;}
    try{
      const {error}=await supabase.auth.updateUser({password:pwDraft.next});
      if(error){setPwError(error.message);return;}
      setPwDone(true);
      setPwDraft({current:"",next:"",confirm:""});
      setTimeout(()=>{setShowPwChange(false);setPwDone(false);},2000);
    }catch(e){
      setPwError(e?.message||"Failed to update password.");
    }
  };

  // MFA: load factors on mount
  useEffect(()=>{
    supabase.auth.mfa.listFactors().then(({data})=>{
      setMfaFactors(data?.totp?.filter(f=>f.status==="verified")||[]);
    });
  },[]);

  const handleMfaEnroll=async()=>{
    setMfaError("");setMfaSuccess("");setMfaEnrolling(true);
    try{
      const {data,error}=await supabase.auth.mfa.enroll({factorType:"totp",issuer:"Orbit"});
      if(error) throw error;
      setMfaQr(data.totp.qr_code);
      setMfaSecret(data.totp.secret);
      setMfaFactorId(data.id);
    }catch(e){
      setMfaError(e?.message||"Failed to start MFA enrollment.");
      setMfaEnrolling(false);
    }
  };

  const handleMfaVerify=async()=>{
    setMfaError("");
    if(mfaVerifyCode.length!==6){setMfaError("Enter the 6-digit code from your authenticator app.");return;}
    try{
      const {data:challenge,error:chalErr}=await supabase.auth.mfa.challenge({factorId:mfaFactorId});
      if(chalErr) throw chalErr;
      const {error:verErr}=await supabase.auth.mfa.verify({factorId:mfaFactorId,challengeId:challenge.id,code:mfaVerifyCode});
      if(verErr) throw verErr;
      setMfaSuccess("Two-factor authentication enabled!");
      setMfaEnrolling(false);setMfaQr(null);setMfaSecret(null);setMfaVerifyCode("");
      const {data}=await supabase.auth.mfa.listFactors();
      setMfaFactors(data?.totp?.filter(f=>f.status==="verified")||[]);
    }catch(e){
      setMfaError(e?.message||"Verification failed. Check the code and try again.");
    }
  };

  const handleMfaUnenroll=async(factorId)=>{
    setMfaError("");setMfaSuccess("");
    try{
      const {error}=await supabase.auth.mfa.unenroll({factorId});
      if(error) throw error;
      setMfaFactors(prev=>prev.filter(f=>f.id!==factorId));
      setMfaSuccess("Two-factor authentication disabled.");
      setTimeout(()=>setMfaSuccess(""),3000);
    }catch(e){
      setMfaError(e?.message||"Failed to disable MFA.");
    }
  };

  // Privacy + notifications + per-person overrides — all from PeopleContext
  const getPersonPriv=(id)=>privOverrides[id]||{privOver:null,notif:true};
  const setPersonPriv=(id,patch)=>setPrivOverrides(o=>({...o,[id]:{...getPersonPriv(id),...patch}}));

  // Data
  const [exportDone,setExportDone]=useState({});
  const [deletePhase,setDeletePhase]=useState('idle'); // 'idle'|'confirming'|'loading'|'error'|'success'
  const [deleteInput,setDeleteInput]=useState('');
  const [deleteError,setDeleteError]=useState('');

  // Bug report
  const [bugDraft,setBugDraft]=useState({category:"bug",title:"",description:"",steps:"",severity:"medium"});
  const [bugSubmitting,setBugSubmitting]=useState(false);
  const [bugResult,setBugResult]=useState(null); // "success" | error string

  const secs=[
    {id:"profile",      icon:"👤",label:"Profile"},
    {id:"privacy",      icon:"🔒",label:"Privacy"},
    {id:"people",       icon:"👥",label:"People"},
    {id:"agreements",   icon:"📝",label:"Agreements"},
    {id:"notifications",icon:"🔔",label:"Notifications"},
    {id:"preferences",  icon:"🎛️",label:"Preferences", swatch:true},
    {id:"calendars",    icon:"📅",label:"Calendars"},
    {id:"work",         icon:"💼",label:"Work Schedule"},
    {id:"data",         icon:"💾",label:"Data"},
    {id:"bugs",         icon:"🐛",label:"Report a Bug"},
  ];
  const ini=(profileName.trim().split(/\s+/).filter(Boolean));
  const avatarIni=ini.length>=2?(ini[0][0]+ini[ini.length-1][0]).toUpperCase():ini[0]?.slice(0,2).toUpperCase()||"?";

  const handleExport=async(key)=>{
    if(key!=="json"){
      // ICS and email/PDF export not yet implemented
      setExportDone(d=>({...d,[key]:"soon"}));
      return;
    }
    setExportDone(d=>({...d,[key]:"exporting"}));
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session?.access_token) throw new Error("Not authenticated");
      const res=await apiFetch("/api/data/export",{headers:{Authorization:`Bearer ${session.access_token}`}});
      if(!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob=await res.blob();
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");
      a.href=url;
      a.download=`orbit-export-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportDone(d=>({...d,[key]:"done"}));
    }catch(e){
      setExportDone(d=>({...d,[key]:"error"}));
    }
  };

  const handleDeleteAccount=async()=>{
    setDeletePhase('loading');
    setDeleteError('');
    try{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session?.access_token){setDeletePhase('error');setDeleteError('Not signed in.');return;}
      const res=await apiFetch('/api/data/delete-account',{
        method:'POST',
        headers:{Authorization:`Bearer ${session.access_token}`},
        signal:AbortSignal.timeout(20_000),
      });
      // 401 here means the auth user was already deleted (e.g. server succeeded
      // but the network dropped before the response arrived). Treat as success.
      if(res.status===401){
        setDeletePhase('success');
        await supabase.auth.signOut().catch(()=>{});
        window.location.href='/';
        return;
      }
      if(!res.ok){
        const body=await res.json().catch(()=>({}));
        setDeletePhase('error');
        setDeleteError(body.error||'Deletion failed. Please try again.');
        return;
      }
      setDeletePhase('success');
      await supabase.auth.signOut().catch(()=>{});
      window.location.href='/';
    }catch(err){
      setDeletePhase('error');
      setDeleteError(err?.name==='TimeoutError'?'Request timed out. Please try again.':'An unexpected error occurred. Please try again.');
    }
  };

  // Mobile: show section list when section===null, else show section content
  const [mobileSection,setMobileSection]=useState(initialSection||null);
  const activeSection=isMobile?mobileSection:section;
  const setActiveSection=isMobile?setMobileSection:setSection;

  if(isMobile&&!mobileSection){
    // Mobile top-level list
    return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{padding:"16px 18px 8px",flexShrink:0}}>
        <h2 style={{fontSize:22,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink}}>Settings</h2>
      </div>
      {/* Profile preview at top */}
      <div style={{margin:"0 16px 8px",background:T.surface,borderRadius:16,border:`1px solid ${T.edge}`,padding:"14px 16px",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}} onClick={()=>setMobileSection("profile")}>
        <div style={{width:44,height:44,borderRadius:"50%",background:`linear-gradient(135deg,${T.partner},${T.coparent})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontFamily:"'Crimson Pro',serif",fontWeight:700,color:"white",flexShrink:0}}>{avatarIni}</div>
        <div style={{flex:1}}>
          <p style={{fontSize:15,fontWeight:600,color:T.ink}}>{profileName}</p>
          <p style={{fontSize:12,color:T.muted}}>{profileEmail}</p>
        </div>
        <span style={{color:T.muted,fontSize:16}}>›</span>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"0 16px 24px"}}>
        {/* Group sections into categories */}
        {[
          {group:"Connections & privacy",items:secs.filter(s=>["privacy","people","agreements"].includes(s.id))},
          {group:"Notifications & features",items:secs.filter(s=>["notifications","preferences"].includes(s.id))},
          {group:"Integrations",items:secs.filter(s=>["calendars","work"].includes(s.id))},
          {group:"Account",items:secs.filter(s=>["data","bugs"].includes(s.id))},
        ].map(({group,items})=>(
          <div key={group} style={{marginBottom:16}}>
            <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,paddingLeft:4}}>{group}</p>
            <div style={{background:T.surface,borderRadius:14,border:`1px solid ${T.edge}`,overflow:"hidden"}}>
              {items.map((s,i)=>(
                <button key={s.id} onClick={()=>setMobileSection(s.id)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"13px 16px",border:"none",background:"transparent",cursor:"pointer",borderBottom:i<items.length-1?`1px solid ${T.edge}`:"none",textAlign:"left"}}>
                  <div style={{width:30,height:30,borderRadius:8,background:T.partner+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{s.icon}</div>
                  <span style={{fontSize:14,fontWeight:500,color:T.ink,flex:1}}>{s.label}</span>
                  {s.swatch&&<span style={{width:10,height:10,borderRadius:"50%",background:T.partner,display:"inline-block",flexShrink:0,marginRight:6,border:`1.5px solid ${T.partner}44`}}/>}
                  <span style={{color:T.muted,fontSize:16}}>›</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>);
  }

  return(<div style={{flex:1,display:"flex",overflow:"hidden"}}>
    {/* Sidebar — desktop */}
    {!isMobile&&<div style={{width:190,flexShrink:0,borderRight:`1px solid ${T.edge}`,background:T.surface,display:"flex",flexDirection:"column",padding:"12px 8px",overflowY:"auto"}}>
      {secs.map(s=>(
        <button key={s.id} onClick={()=>setActiveSection(s.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:10,border:"none",cursor:"pointer",background:activeSection===s.id?T.partner+"12":"transparent",color:activeSection===s.id?T.partner:T.bark,fontSize:12,fontWeight:activeSection===s.id?700:400,textAlign:"left",marginBottom:2,transition:"background .12s",width:"100%"}}>
          <span style={{fontSize:14,color:activeSection===s.id?T.partner:T.muted}}>{s.icon}</span>
          <span style={{flex:1}}>{s.label}</span>
          {s.swatch&&<span style={{width:8,height:8,borderRadius:"50%",background:T.partner,display:"inline-block",flexShrink:0,opacity:activeSection===s.id?0.6:1}}/>}
        </button>
      ))}
    </div>}

    <div style={{flex:1,overflowY:"auto",padding:isMobile?"0":"16px 24px"}}>
      {/* Mobile section header with back button */}
      {isMobile&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"12px 16px 8px",flexShrink:0,borderBottom:`1px solid ${T.edge}`,marginBottom:16}}>
        <button onClick={()=>setMobileSection(null)} style={{width:30,height:30,borderRadius:"50%",background:T.mist,border:"none",cursor:"pointer",color:T.bark,fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>←</button>
        <h2 style={{fontSize:18,fontFamily:"'Crimson Pro',serif",fontWeight:400,color:T.ink}}>{secs.find(s=>s.id===mobileSection)?.label}</h2>
      </div>}
      <div style={{padding:isMobile?"0 16px 24px":"0"}}>

      {/* ── Profile ── */}
      {activeSection==="profile"&&(<div>
        <Card>
          <CardHead icon="👤" label="Your profile"/>
          {!editing?(
            <div style={{padding:"14px 16px"}}>
              <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:profileBirthday?10:0}}>
                <div style={{width:52,height:52,borderRadius:"50%",overflow:"hidden",background:`linear-gradient(135deg,${T.partner},${T.coparent})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:19,fontFamily:"'Crimson Pro',serif",fontWeight:700,color:"white",flexShrink:0}}>
                  {profilePhoto?<img src={profilePhoto} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:avatarIni}
                </div>
                <div style={{flex:1}}>
                  <p style={{fontSize:15,fontWeight:600,color:T.ink,fontFamily:"'Crimson Pro',serif"}}>{profileName}</p>
                  <p style={{fontSize:12,color:T.muted}}>{profileEmail} · {PEOPLE.length} connections</p>
                  {profileBirthday&&<p style={{fontSize:12,color:T.muted,marginTop:2}}>🎂 {(([y,m,d])=>`${m}/${d}/${y}`)(profileBirthday.split("-"))}</p>}
                </div>
                <button onClick={()=>{setProfileDraft({name:profileName,email:profileEmail,birthday:profileBirthday,photo:profilePhoto,fullName:profileFullName});setEditing(true);}} style={{padding:"5px 12px",borderRadius:9,background:T.mist,border:`1px solid ${T.edge}`,fontSize:11,color:T.bark,cursor:"pointer"}}>Edit</button>
              </div>
            </div>
          ):(
            <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
              {/* Photo */}
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:4}}>
                <div style={{width:52,height:52,borderRadius:"50%",overflow:"hidden",background:`linear-gradient(135deg,${T.partner},${T.coparent})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontFamily:"'Crimson Pro',serif",fontWeight:700,color:"white",flexShrink:0}}>
                  {profileDraft.photo?<img src={profileDraft.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:avatarIni}
                </div>
                {hasCameraPlugin()
                  ? <button type="button" style={{padding:"6px 12px",borderRadius:9,background:T.mist,border:`1px solid ${T.edge}`,fontSize:11,color:T.bark,cursor:"pointer"}} onClick={async()=>{try{const url=await pickProfilePhoto();setProfileDraft(d=>({...d,photo:url}));}catch(e){const msg=e?.message||String(e);if(!msg.includes('cancelled')&&!msg.includes('canceled')&&!msg.includes('dismissed')){console.error('[photo] pick failed:',e);alert('Could not open photo picker: '+msg);}}}}>Change photo</button>
                  : <label style={{padding:"6px 12px",borderRadius:9,background:T.mist,border:`1px solid ${T.edge}`,fontSize:11,color:T.bark,cursor:"pointer",position:"relative"}}>
                      Change photo
                      <input type="file" accept="image/*" onChange={handlePhotoSelect} style={{opacity:0,position:"absolute",width:0,height:0}}/>
                    </label>
                }
                {profileDraft.photo&&<button onClick={()=>setProfileDraft(d=>({...d,photo:null}))} style={{padding:"6px 10px",borderRadius:9,background:"transparent",border:`1px solid ${T.edge}`,fontSize:11,color:T.muted,cursor:"pointer"}}>Remove</button>}
              </div>
              <div>
                <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4}}>Display name</label>
                <input value={profileDraft.name} onChange={e=>setProfileDraft(d=>({...d,name:e.target.value}))} placeholder="How you appear in the app" style={{width:"100%",padding:"9px 11px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4}}>Full name</label>
                <input value={profileDraft.fullName} onChange={e=>setProfileDraft(d=>({...d,fullName:e.target.value}))} placeholder="Legal / full name" style={{width:"100%",padding:"9px 11px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4}}>Email</label>
                <input value={profileDraft.email} onChange={e=>setProfileDraft(d=>({...d,email:e.target.value}))} type="email" style={{width:"100%",padding:"9px 11px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4}}>Birthday</label>
                <input value={profileDraft.birthday} onChange={e=>setProfileDraft(d=>({...d,birthday:e.target.value}))} type="date" style={{width:"100%",padding:"9px 11px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box"}}/>
              </div>
              <div style={{display:"flex",gap:8,marginTop:4}}>
                <button onClick={handleProfileSave} style={{flex:2,padding:"9px",borderRadius:10,background:T.bark,border:"1px solid transparent",color:"white",fontSize:12,fontWeight:600,cursor:"pointer"}}>Save changes</button>
                <button onClick={()=>setEditing(false)} style={{flex:1,padding:"9px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
              </div>
            </div>
          )}
        </Card>
        {/* Password change */}
        <Card>
          <CardHead icon="🔒" label="Password"/>
          {!showPwChange?(
            <div style={{padding:"10px 16px"}}>
              <button onClick={()=>{setShowPwChange(true);setPwError("");setPwDone(false);}} style={{padding:"8px 14px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,fontSize:12,color:T.bark,cursor:"pointer"}}>Change password…</button>
            </div>
          ):(
            <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
              <div>
                <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4}}>New password</label>
                <input value={pwDraft.next} onChange={e=>setPwDraft(d=>({...d,next:e.target.value}))} type="password" placeholder="At least 6 characters" style={{width:"100%",padding:"9px 11px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4}}>Confirm password</label>
                <input value={pwDraft.confirm} onChange={e=>setPwDraft(d=>({...d,confirm:e.target.value}))} type="password" placeholder="Repeat new password" style={{width:"100%",padding:"9px 11px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box"}}/>
              </div>
              {pwError&&<p style={{fontSize:12,color:T.red,background:T.red+"12",padding:"8px 10px",borderRadius:9,border:`1px solid ${T.red}30`}}>{pwError}</p>}
              {pwDone&&<p style={{fontSize:12,color:T.sage,background:T.sage+"12",padding:"8px 10px",borderRadius:9,border:`1px solid ${T.sage}30`}}>Password updated!</p>}
              <div style={{display:"flex",gap:8}}>
                <button onClick={handlePasswordChange} style={{flex:2,padding:"9px",borderRadius:10,background:T.bark,border:"none",color:"white",fontSize:12,fontWeight:600,cursor:"pointer"}}>Update password</button>
                <button onClick={()=>{setShowPwChange(false);setPwError("");}} style={{flex:1,padding:"9px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
              </div>
            </div>
          )}
        </Card>
        {/* Two-factor authentication */}
        <Card>
          <CardHead icon="🔐" label="Two-Factor Authentication"/>
          <div style={{padding:"14px 16px"}}>
            {mfaFactors===null?(
              <p style={{fontSize:12,color:T.muted}}>Loading…</p>
            ):mfaEnrolling&&mfaQr?(
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <p style={{fontSize:12,color:T.ink,lineHeight:1.5}}>Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)</p>
                <div style={{textAlign:"center"}}><img src={mfaQr} alt="MFA QR Code" style={{width:180,height:180,borderRadius:12,border:`1px solid ${T.edge}`}}/></div>
                {mfaSecret&&<div style={{background:T.mist,borderRadius:8,padding:"8px 10px",textAlign:"center"}}>
                  <p style={{fontSize:10,color:T.muted,marginBottom:3}}>Or enter this key manually:</p>
                  <p style={{fontSize:12,fontFamily:"monospace",color:T.bark,letterSpacing:1,wordBreak:"break-all"}}>{mfaSecret}</p>
                </div>}
                <div>
                  <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.06em",display:"block",marginBottom:4}}>Verification code</label>
                  <input value={mfaVerifyCode} onChange={e=>setMfaVerifyCode(e.target.value.replace(/\D/g,"").slice(0,6))} type="text" inputMode="numeric" maxLength={6} placeholder="000000" autoFocus style={{width:"100%",padding:"9px 11px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:18,color:T.ink,fontFamily:"monospace",textAlign:"center",letterSpacing:6,boxSizing:"border-box"}}/>
                </div>
                {mfaError&&<p style={{fontSize:12,color:T.red,background:T.red+"12",padding:"8px 10px",borderRadius:9,border:`1px solid ${T.red}30`}}>{mfaError}</p>}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={handleMfaVerify} style={{flex:2,padding:"9px",borderRadius:10,background:T.bark,border:"none",color:"white",fontSize:12,fontWeight:600,cursor:"pointer"}}>Verify & enable</button>
                  <button onClick={()=>{setMfaEnrolling(false);setMfaQr(null);setMfaSecret(null);setMfaVerifyCode("");setMfaError("");if(mfaFactorId)supabase.auth.mfa.unenroll({factorId:mfaFactorId}).catch(()=>{});}} style={{flex:1,padding:"9px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
                </div>
              </div>
            ):mfaFactors.length>0?(
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:T.sage}}/>
                  <p style={{fontSize:13,color:T.ink,fontWeight:600}}>Enabled</p>
                  <p style={{fontSize:11,color:T.muted,marginLeft:"auto"}}>Authenticator app</p>
                </div>
                {mfaSuccess&&<p style={{fontSize:12,color:T.sage,background:T.sage+"12",padding:"8px 10px",borderRadius:9,border:`1px solid ${T.sage}30`}}>{mfaSuccess}</p>}
                {mfaError&&<p style={{fontSize:12,color:T.red,background:T.red+"12",padding:"8px 10px",borderRadius:9,border:`1px solid ${T.red}30`}}>{mfaError}</p>}
                <button onClick={()=>handleMfaUnenroll(mfaFactors[0].id)} style={{padding:"8px 14px",borderRadius:10,background:T.red+"14",border:`1px solid ${T.red}35`,fontSize:12,color:T.red,cursor:"pointer",alignSelf:"flex-start"}}>Disable two-factor</button>
              </div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <p style={{fontSize:12,color:T.muted,lineHeight:1.5}}>Add an extra layer of security by requiring a code from your authenticator app when you log in.</p>
                {mfaSuccess&&<p style={{fontSize:12,color:T.sage,background:T.sage+"12",padding:"8px 10px",borderRadius:9,border:`1px solid ${T.sage}30`}}>{mfaSuccess}</p>}
                {mfaError&&<p style={{fontSize:12,color:T.red,background:T.red+"12",padding:"8px 10px",borderRadius:9,border:`1px solid ${T.red}30`}}>{mfaError}</p>}
                <button onClick={handleMfaEnroll} style={{padding:"8px 14px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,fontSize:12,color:T.bark,cursor:"pointer",alignSelf:"flex-start"}}>Set up two-factor…</button>
              </div>
            )}
          </div>
        </Card>
        <Card>
          <CardHead icon="👤" label="Account"/>
          <Row
            label="Take the tour"
            sub="Replay the getting-started walkthrough"
            right={
              <button
                onClick={()=>{if(onTakeTour){try{localStorage.removeItem("orbit_walkthrough_done");}catch{}onTakeTour();}}}
                style={{padding:"6px 10px",borderRadius:10,background:T.clay+"14",border:`1px solid ${T.clay}35`,fontSize:11,color:T.clay,fontWeight:700,cursor:"pointer"}}
              >
                Start →
              </button>
            }
          />
          <Row
            label="Log out"
            sub="Sign out of this device"
            right={
              <button
                onClick={async()=>{try{const {data:{session}}=await supabase.auth.getSession();await cleanupPushToken(session?.access_token);await supabase.auth.signOut();}catch(e){}}}
                style={{padding:"6px 10px",borderRadius:10,background:T.red+"14",border:`1px solid ${T.red}35`,fontSize:11,color:T.red,fontWeight:700,cursor:"pointer"}}
              >
                Log out
              </button>
            }
            border={false}
          />
        </Card>
      </div>)}

      {/* ── Privacy ── */}
      {activeSection==="privacy"&&(<div>
        <Card>
          <CardHead icon="🔒" label="Privacy defaults" sub="What you share by relationship type"/>
          {Object.entries(REL).map(([key,cfg],i,arr)=>(
            <Row key={key}
              label={<span style={{display:"flex",alignItems:"center",gap:7}}><span style={{color:cfg.color}}>{cfg.icon}</span>{cfg.label}</span>}
              sub="Default sharing level"
              border={i<arr.length-1}
              right={<select value={priv[key]} onChange={e=>setPriv(p=>({...p,[key]:e.target.value}))} style={{padding:"4px 8px",borderRadius:8,border:`1px solid ${T.edge}`,background:T.mist,fontSize:11,color:cfg.color,fontWeight:600,cursor:"pointer"}}>
                {PL.map(o=><option key={o.id} value={o.id}>{o.icon} {o.l}</option>)}
              </select>}
            />
          ))}
        </Card>
        <Card>
          <CardHead icon="🛡️" label="Global rules"/>
          <Row label="Hide when offline" sub="Don't show availability after midnight" right={<Tog on={hideOffline} set={setHideOffline} c={T.sage}/>}/>
          <Row label="Block personal notes" sub="Never share event descriptions" right={<Tog on={blockNotes} set={setBlockNotes} c={T.sage}/>} border={false}/>
        </Card>
      </div>)}

      {/* ── People ── */}
      {activeSection==="people"&&(<div>
        <Card>
          <CardHead icon="🔗" label="Invite someone to orbit"/>
          <div style={{padding:"12px 14px"}}>
            <p style={{fontSize:12,color:T.muted,marginBottom:10,lineHeight:1.6}}>Generate a link to share — they'll be guided through setup and added to your orbit automatically. Links expire after 72 hours.</p>
            {!inviteLink?(
              <>
                <div style={{display:"flex",gap:8,marginBottom:inviteGenError?6:0}}>
                  <select value={inviteRelType} onChange={e=>setInviteRelType(e.target.value)} style={{flex:1,padding:"7px 10px",borderRadius:9,border:`1px solid ${T.edge}`,background:T.mist,fontSize:12,color:T.bark,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                    {Object.entries(REL).map(([k,cfg])=><option key={k} value={k}>{cfg.icon} {cfg.label}</option>)}
                  </select>
                  <button onClick={generateInvite} disabled={inviteGenerating} style={{padding:"7px 16px",borderRadius:9,background:inviteGenerating?T.edgeMid:T.bark,border:"none",color:T.parchment,fontSize:12,fontWeight:600,cursor:inviteGenerating?"default":"pointer",transition:"background .15s"}}>
                    {inviteGenerating?"…":"Get link"}
                  </button>
                </div>
                {inviteGenError&&<p style={{fontSize:11,color:T.red,marginTop:4}}>{inviteGenError}</p>}
              </>
            ):(
              <>
                <div style={{display:"flex",gap:7,marginBottom:6}}>
                  <input readOnly value={inviteLink} onClick={e=>e.target.select()} style={{flex:1,padding:"7px 10px",borderRadius:9,border:`1px solid ${T.edge}`,background:T.mist,fontSize:11,color:T.bark,fontFamily:"monospace",minWidth:0}}/>
                  <button onClick={copyInviteLink} style={{padding:"7px 14px",borderRadius:9,background:inviteCopied?T.sage:T.bark,border:"none",color:"white",fontSize:11,fontWeight:600,cursor:"pointer",transition:"background .2s",flexShrink:0}}>
                    {inviteCopied?"✓ Copied":"Copy"}
                  </button>
                </div>
                <p style={{fontSize:11,color:T.muted,marginBottom:6}}>Share via text, email, or any messaging app. Expires in 72 hours.</p>
                <button onClick={()=>{setInviteLink(null);setInviteCopied(false);}} style={{fontSize:11,color:T.muted,background:"none",border:"none",cursor:"pointer",textDecoration:"underline",textUnderlineOffset:2,padding:0}}>Generate a new link</button>
              </>
            )}
          </div>
        </Card>
        {Object.entries(REL).map(([key,cfg])=>{
          const grp=PEOPLE.filter(p=>p.type===key);
          if(!grp.length) return null;
          return(<Card key={key}>
            <CardHead icon={cfg.icon} label={`${cfg.plural} (${grp.length})`} sub={`Override privacy per ${cfg.label.toLowerCase()}`}/>
            {grp.map((p,i)=>{
              const pp=getPersonPriv(p.id);
              return(<div key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:i<grp.length-1?`1px solid ${T.edge}`:"none"}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:cfg.color+"20",border:`1.5px solid ${cfg.color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:cfg.color,fontFamily:"'Crimson Pro',serif",flexShrink:0}}>{p.ini}</div>
                <p style={{fontSize:13,fontWeight:500,color:T.ink,flex:1}}>{p.name}</p>
                <select value={pp.privOver||priv[key]} onChange={e=>setPersonPriv(p.id,{privOver:e.target.value})} style={{padding:"3px 7px",borderRadius:8,border:`1px solid ${T.edge}`,background:T.mist,fontSize:11,color:cfg.color,marginRight:8,cursor:"pointer"}}>
                  {PL.map(o=><option key={o.id} value={o.id}>{o.icon} {o.l}</option>)}
                </select>
                <span style={{fontSize:11,color:T.muted,marginRight:4}} title="Receive notifications when this person updates their availability or sends a scheduling proposal">Notifs</span>
                <Tog on={pp.notif} set={v=>setPersonPriv(p.id,{notif:v})} c={T.sage}/>
              </div>);
            })}
          </Card>);
        })}
        {PEOPLE.length===0&&<div style={{textAlign:"center",padding:"48px 24px",color:T.muted,fontSize:13}}>No connections yet — add people from the People tab.</div>}
      </div>)}

      {/* ── Notifications ── */}
      {activeSection==="notifications"&&(<div>
        <Card>
          <CardHead icon="🔔" label="In-app notifications"/>
          {Object.entries({proposals:"Date proposals",confirmations:"Confirmations",reminders:"Reminders"}).map(([k,l],i,arr)=>(
            <Row key={k} label={l} right={<Tog on={notifSettings[k]} set={v=>setNotifSettings(n=>({...n,[k]:v}))} c={T.sage}/>} border={i<arr.length-1}/>
          ))}
        </Card>
        <Card>
          <CardHead icon="❤️" label="In-app health alerts"/>
          {Object.entries({fairness:"Fairness alerts",drift:"Drift nudges",anchors:"Anchor reminders"}).map(([k,l],i,arr)=>(
            <Row key={k} label={l} right={<Tog on={notifSettings[k]} set={v=>setNotifSettings(n=>({...n,[k]:v}))} c={T.sage}/>} border={i<arr.length-1}/>
          ))}
        </Card>
        <NotifChannelPrefs/>
        <FairnessFreqCard/>
      </div>)}

      {/* ── Preferences ── */}
      {activeSection==="preferences"&&(<div>
        <Card>
          <CardHead icon="🎛️" label="Features"/>
          {Object.entries({fairnessTracking:"Fairness tracking",anchorNights:"Anchor nights",coparentCal:"Coparent calendar"}).map(([k,l],i,arr)=>(
            <Row key={k} label={l} right={<Tog on={prefs[k]} set={v=>setPrefs(p=>({...p,[k]:v}))} c={T.sage}/>} border={i<arr.length-1}/>
          ))}
        </Card>
        <AppearanceCard/>
        <Card>
          <CardHead icon="📱" label="Default views"/>
          <Row label="Home tab" sub="What to show first" right={
            <select style={{padding:"4px 8px",borderRadius:8,border:`1px solid ${T.edge}`,background:T.mist,fontSize:11,color:T.bark,cursor:"pointer"}}>
              <option>Overview</option><option>Schedule</option><option>Fairness</option>
            </select>
          }/>
          <Row label="Schedule mode" sub="1:1 or group by default" border={false} right={
            <select style={{padding:"4px 8px",borderRadius:8,border:`1px solid ${T.edge}`,background:T.mist,fontSize:11,color:T.bark,cursor:"pointer"}}>
              <option>1:1</option><option>Group</option>
            </select>
          }/>
        </Card>
      </div>)}

      {/* ── Agreements ── */}
      {activeSection==="agreements"&&(<div><AgreementsTab/></div>)}

      {/* ── Calendars ── */}
      {activeSection==="calendars"&&(<CalendarsSection isMobile={isMobile} googleOAuthError={googleOAuthError} outlookOAuthError={outlookOAuthError} yahooOAuthError={yahooOAuthError} caldavConnectError={caldavConnectError}/>)}

      {/* ── Work Schedule ── */}
      {activeSection==="work"&&(<WorkScheduleSection/>)}

      {/* ── Data ── */}
      {activeSection==="data"&&(<div>
        <Card>
          <CardHead icon="💾" label="Your data"/>
          {[
            {key:"json", l:"Export all data",    s:"Download as JSON"},
            {key:"ics",  l:"Export calendar",    s:"Download as .ics file"},
            {key:"email",l:"Account summary",    s:"Email a PDF summary"},
          ].map((r,i)=>{
            const state=exportDone[r.key];
            return(
            <Row key={r.key} label={r.l} sub={r.s} border={i<2}
              right={
                state==="done"
                  ?<span style={{fontSize:11,color:T.sage,fontWeight:600}}>✓ Done</span>
                  :state==="error"
                  ?<span style={{fontSize:11,color:T.red,fontWeight:600}}>Failed</span>
                  :state==="soon"
                  ?<span style={{fontSize:11,color:T.muted}}>Coming soon</span>
                  :<button onClick={()=>handleExport(r.key)} style={{padding:"4px 10px",borderRadius:9,background:state==="exporting"?T.edgeMid:T.mist,border:`1px solid ${T.edge}`,fontSize:11,color:state==="exporting"?T.muted:T.bark,cursor:"pointer",transition:"all .15s"}}>
                    {state==="exporting"?"Preparing…":"Export"}
                  </button>
              }
            />
          );})}

        </Card>
        <Card>
          <CardHead icon="⚠️" label="Danger zone" variant="danger"/>
          {deletePhase==='idle'&&(
            <Row label="Delete account" sub="Permanently remove all your data" border={false}
              right={<button onClick={()=>setDeletePhase('confirming')} style={{padding:"4px 10px",borderRadius:9,background:T.red+"12",border:`1px solid ${T.red}35`,color:T.red,fontSize:11,cursor:"pointer",fontWeight:600}}>Delete</button>}/>
          )}
          {deletePhase==='confirming'&&(
            <div style={{padding:"14px 16px"}}>
              <p style={{fontSize:13,fontWeight:600,color:T.ink,marginBottom:4}}>Delete your account?</p>
              <p style={{fontSize:12,color:T.muted,marginBottom:12}}>This permanently deletes all your data and cannot be undone. Type <strong>DELETE</strong> to confirm.</p>
              <input
                value={deleteInput}
                onChange={e=>setDeleteInput(e.target.value)}
                placeholder="Type DELETE"
                style={{width:"100%",padding:"8px 10px",borderRadius:9,border:`1px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,marginBottom:10,boxSizing:"border-box",outline:"none"}}
              />
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{setDeletePhase('idle');setDeleteInput('');}} style={{flex:1,padding:"8px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,fontSize:12,color:T.bark,cursor:"pointer"}}>Cancel</button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteInput!=='DELETE'}
                  style={{flex:1,padding:"8px",borderRadius:10,background:deleteInput==='DELETE'?T.red:T.red+"40",border:"none",fontSize:12,color:"white",fontWeight:700,cursor:deleteInput==='DELETE'?"pointer":"not-allowed",transition:"background .15s"}}
                >Permanently delete</button>
              </div>
            </div>
          )}
          {deletePhase==='loading'&&(
            <div style={{padding:"14px 16px",textAlign:"center"}}>
              <p style={{fontSize:13,color:T.muted}}>Deleting your account…</p>
            </div>
          )}
          {deletePhase==='error'&&(
            <div style={{padding:"14px 16px"}}>
              <div style={{background:T.red+"12",border:`1px solid ${T.red}30`,borderRadius:10,padding:"10px 12px",marginBottom:10}}>
                <p style={{fontSize:12,color:T.red}}>{deleteError}</p>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{setDeletePhase('idle');setDeleteInput('');setDeleteError('');}} style={{flex:1,padding:"8px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,fontSize:12,color:T.bark,cursor:"pointer"}}>Cancel</button>
                <button onClick={()=>{setDeletePhase('confirming');setDeleteInput('');}} style={{flex:1,padding:"8px",borderRadius:10,background:T.red+"12",border:`1px solid ${T.red}35`,fontSize:12,color:T.red,cursor:"pointer",fontWeight:600}}>Try again</button>
              </div>
            </div>
          )}
          {deletePhase==='success'&&(
            <div style={{padding:"14px 16px",textAlign:"center"}}>
              <p style={{fontSize:13,color:T.muted}}>Account deleted. Signing out…</p>
            </div>
          )}
        </Card>
      </div>)}

      {activeSection==="bugs"&&(<div>
        <Card>
          <CardHead icon="🐛" label="Report a Bug"/>
          <div style={{padding:"14px 16px"}}>
            {bugResult==="success"?(
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:32,marginBottom:8}}>✓</div>
                <p style={{fontSize:14,fontWeight:600,color:T.ink,marginBottom:4}}>Thanks for your report!</p>
                <p style={{fontSize:12,color:T.muted,marginBottom:16}}>We'll look into this as soon as possible.</p>
                <button onClick={()=>{setBugResult(null);setBugDraft({category:"bug",title:"",description:"",steps:"",severity:"medium"});}} style={{padding:"8px 20px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,fontSize:12,color:T.bark,cursor:"pointer",fontWeight:500}}>Report another</button>
              </div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {bugResult&&bugResult!=="success"&&(
                  <div style={{background:T.red+"12",border:`1px solid ${T.red}30`,borderRadius:10,padding:"10px 12px"}}>
                    <p style={{fontSize:12,color:T.red}}>{bugResult}</p>
                  </div>
                )}
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:T.ink,marginBottom:4,display:"block"}}>Category</label>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {[{v:"bug",l:"Bug"},{v:"ui",l:"UI Issue"},{v:"calendar",l:"Calendar"},{v:"scheduling",l:"Scheduling"},{v:"other",l:"Other"}].map(c=>(
                      <button key={c.v} onClick={()=>setBugDraft(d=>({...d,category:c.v}))} style={{padding:"5px 12px",borderRadius:8,fontSize:11,fontWeight:bugDraft.category===c.v?600:400,background:bugDraft.category===c.v?T.clay+"18":T.mist,border:`1px solid ${bugDraft.category===c.v?T.clay+"50":T.edge}`,color:bugDraft.category===c.v?T.clay:T.bark,cursor:"pointer",transition:"all .15s"}}>{c.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:T.ink,marginBottom:4,display:"block"}}>Title</label>
                  <input value={bugDraft.title} onChange={e=>setBugDraft(d=>({...d,title:e.target.value}))} placeholder="Brief summary of the issue" maxLength={200} style={{width:"100%",padding:"8px 12px",borderRadius:10,border:`1px solid ${T.edge}`,background:T.surface,fontSize:13,color:T.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:T.ink,marginBottom:4,display:"block"}}>Description</label>
                  <textarea value={bugDraft.description} onChange={e=>setBugDraft(d=>({...d,description:e.target.value}))} placeholder="What happened? What did you expect to happen?" maxLength={5000} rows={4} style={{width:"100%",padding:"8px 12px",borderRadius:10,border:`1px solid ${T.edge}`,background:T.surface,fontSize:13,color:T.ink,outline:"none",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:T.ink,marginBottom:4,display:"block"}}>Steps to reproduce <span style={{fontWeight:400,color:T.muted}}>(optional)</span></label>
                  <textarea value={bugDraft.steps} onChange={e=>setBugDraft(d=>({...d,steps:e.target.value}))} placeholder="1. Go to...&#10;2. Click on...&#10;3. See error" maxLength={5000} rows={3} style={{width:"100%",padding:"8px 12px",borderRadius:10,border:`1px solid ${T.edge}`,background:T.surface,fontSize:13,color:T.ink,outline:"none",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:500,color:T.ink,marginBottom:4,display:"block"}}>Severity</label>
                  <div style={{display:"flex",gap:6}}>
                    {[{v:"low",l:"Low",c:T.sage},{v:"medium",l:"Medium",c:T.amber},{v:"high",l:"High",c:"#D08040"},{v:"critical",l:"Critical",c:T.red}].map(s=>(
                      <button key={s.v} onClick={()=>setBugDraft(d=>({...d,severity:s.v}))} style={{padding:"5px 12px",borderRadius:8,fontSize:11,fontWeight:bugDraft.severity===s.v?600:400,background:bugDraft.severity===s.v?s.c+"18":T.mist,border:`1px solid ${bugDraft.severity===s.v?s.c+"50":T.edge}`,color:bugDraft.severity===s.v?s.c:T.bark,cursor:"pointer",transition:"all .15s"}}>{s.l}</button>
                    ))}
                  </div>
                </div>
                <button disabled={bugSubmitting||!bugDraft.title.trim()||!bugDraft.description.trim()} onClick={async()=>{
                  setBugSubmitting(true);setBugResult(null);
                  try{
                    const {data:{session}}=await supabase.auth.getSession();
                    const res=await apiFetch("/api/bugs/submit",{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${session?.access_token}`},body:JSON.stringify(bugDraft)});
                    const json=await res.json();
                    if(!res.ok) throw new Error(json.error||"Failed to submit");
                    setBugResult("success");
                  }catch(e){setBugResult(e.message||"Something went wrong");}
                  finally{setBugSubmitting(false);}
                }} style={{padding:"10px",borderRadius:12,border:"none",background:(!bugDraft.title.trim()||!bugDraft.description.trim())?T.edgeMid:T.clay,color:"white",fontSize:13,fontWeight:700,cursor:(!bugDraft.title.trim()||!bugDraft.description.trim())?"not-allowed":"pointer",opacity:bugSubmitting?0.6:1,transition:"all .15s"}}>
                  {bugSubmitting?"Submitting…":"Submit Bug Report"}
                </button>
              </div>
            )}
          </div>
        </Card>
      </div>)}
    </div>
    </div>
  </div>);
}

// ── Calendars section (lives inside Settings) ────────────────────────────────
const CAL_PROVIDERS=[
  {id:"icloud",   label:"iCloud",           icon:"☁️",  color:"#1C7BE0", domain:"icloud.com",
   calendars:["Home","Work","Birthdays","Holidays in United States"]},
  {id:"google",   label:"Google",           icon:"🗓️",  color:"#EA4335", domain:"gmail.com",
   calendars:["My Calendar","Work","Birthdays","Reminders"]},
  {id:"yahoo",    label:"Yahoo!",           icon:"💜",  color:"#6001D2", domain:"yahoo.com",
   calendars:[]},
  {id:"outlook",  label:"Outlook.com",      icon:"📆",  color:"#0078D4", domain:"outlook.com",
   calendars:["Calendar","Birthdays","United States holidays"]},
  {id:"ofw",      label:"Our Family Wizard",  icon:"👶", color:T.coparent,domain:"ourfamilywizard.com",
   calendars:[]},
  {id:"caldav",   label:"Other CalDAV server",icon:"📅", color:"#5A7BAE", domain:"",
   calendars:[]},
];

function CalConnectErrorBanner({error, calStatus, title, hint}) {
  if (!error || (calStatus !== null && calStatus !== "error")) return null;
  return (
    <div style={{background:T.red+"12",border:`1px solid ${T.red}30`,borderRadius:12,padding:"12px 14px",marginBottom:12}}>
      <p style={{fontSize:13,fontWeight:600,color:T.red,marginBottom:4}}>{title}</p>
      <p style={{fontSize:12,color:T.red,lineHeight:1.5}}>{decodeURIComponent(error).replace(/_/g," ")}</p>
      <p style={{fontSize:11,color:T.mid,marginTop:6}}>{hint}</p>
    </div>
  );
}

function CalendarsSection({isMobile,googleOAuthError,outlookOAuthError,yahooOAuthError,caldavConnectError}){
  const {googleCalStatus,userId,setGoogleCalAvail,setGoogleCalStatus,setCalEvents,fetchGoogleCalEvents,googleAccounts,setGoogleAccounts,calEvents,icloudCalStatus,setIcloudCalStatus,icloudCalendars,setIcloudCalendars,icloudSelectedCalIds,setIcloudSelectedCalIds,fetchICloudCalEvents,outlookCalStatus,setOutlookCalStatus,outlookCalendars,setOutlookCalendars,outlookSelectedCalIds,setOutlookSelectedCalIds,fetchOutlookCalEvents,ofwCalStatus,setOfwCalStatus,ofwUploadedAt,setOfwUploadedAt,fetchOfwCalEvents,yahooCalStatus,setYahooCalStatus,yahooCalendars,setYahooCalendars,yahooSelectedCalIds,setYahooSelectedCalIds,fetchYahooCalEvents,caldavCalStatus,setCaldavCalStatus,caldavCalendars,setCaldavCalendars,caldavSelectedCalIds,setCaldavSelectedCalIds,fetchCaldavCalEvents,calendarCustomNames,setCalendarCustomNames,prefs,accounts,setAccounts}=usePeople();
  const [calListLoading,setCalListLoading]=useState(false);
  // Track which provider cards are collapsed to reduce scrolling
  // Set tracks *expanded* providers — everything starts collapsed
  const [expandedProviders,setExpandedProviders]=useState(new Set());
  const toggleCollapse=(key)=>setExpandedProviders(prev=>{const next=new Set(prev);if(next.has(key))next.delete(key);else next.add(key);return next;});

  // Track when each provider first became "connected" in this session, so we
  // can show a "last synced" timestamp in the UI.
  const [syncedAt,setSyncedAt]=useState({});
  useEffect(()=>{
    const providers={google:googleCalStatus,outlook:outlookCalStatus,yahoo:yahooCalStatus,caldav:caldavCalStatus,icloud:icloudCalStatus};
    Object.entries(providers).forEach(([k,s])=>{
      if(s==="connected"&&!syncedAt[k]) setSyncedAt(prev=>({...prev,[k]:Date.now()}));
    });
  },[googleCalStatus,outlookCalStatus,yahooCalStatus,caldavCalStatus,icloudCalStatus]); // syncedAt intentionally omitted — write-once

  function fmtSynced(ts){
    if(!ts) return "";
    const mins=Math.round((Date.now()-ts)/60000);
    if(mins<1) return "synced just now";
    if(mins===1) return "synced 1 min ago";
    if(mins<60) return `synced ${mins} min ago`;
    const hrs=Math.round(mins/60);
    return `synced ${hrs}h ago`;
  }

  // Fetch all connected Google accounts and their calendar lists when connected
  // and not yet loaded. Lives in context so it survives Settings remounts.
  useEffect(()=>{
    if(googleCalStatus!=="connected"||calListLoading||googleAccounts!==null) return;
    setCalListLoading(true);
    (async()=>{
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(!session?.access_token) return;
        const res=await apiFetch("/api/google/calendars",{headers:{Authorization:`Bearer ${session.access_token}`}});
        if(!res.ok) return;
        const json=await res.json();
        const token=session.access_token;
        let anyAutoExpanded=false;
        const loaded=await Promise.all((json.accounts||[]).map(async acc=>{
          const cals=acc.calendars||[];
          const primaryCal=cals.find(c=>c.primary);
          // null means never configured — default to ALL calendars and persist
          const neverConfigured=acc.selected_calendar_ids===null;
          let selIds;
          if(neverConfigured&&cals.length>0){
            selIds=cals.map(c=>c.id);
            anyAutoExpanded=true;
            try{await apiFetch("/api/google/calendars",{method:"PATCH",headers:{Authorization:`Bearer ${token}`,"Content-Type":"application/json"},body:JSON.stringify({account_email:acc.email,selected_calendar_ids:selIds})});}catch{}
          }else{
            selIds=acc.selected_calendar_ids||[];
            // Resolve 'primary' alias to real calendar ID so toggle checks work
            if(primaryCal&&selIds.includes("primary")){
              selIds=selIds.map(id=>id==="primary"?primaryCal.id:id);
            }
          }
          return{email:acc.email,status:acc.status,calendars:cals,selectedCalIds:selIds};
        }));
        setGoogleAccounts(loaded);
        if(anyAutoExpanded) fetchGoogleCalEvents(token);
      }catch{}
      finally{setCalListLoading(false);}
    })();
  },[googleCalStatus,googleAccounts,calListLoading]);

  const toggleGoogleCal=async(accountEmail,calId)=>{
    const {data:{session}}=await supabase.auth.getSession();
    if(!session?.access_token) return;
    // Compute next selection outside the setState updater
    const account=googleAccounts?.find(a=>a.email===accountEmail);
    if(!account) return;
    const next=account.selectedCalIds.includes(calId)
      ?account.selectedCalIds.filter(id=>id!==calId)
      :[...account.selectedCalIds,calId];
    // Optimistic update
    setGoogleAccounts(prev=>prev?.map(a=>a.email!==accountEmail?a:{...a,selectedCalIds:next}));
    // Await PATCH so the server has the new selection before events are refetched
    await apiFetch("/api/google/calendars",{
      method:"PATCH",
      headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},
      body:JSON.stringify({account_email:accountEmail,selected_calendar_ids:next}),
    });
    fetchGoogleCalEvents(session.access_token);
  };
  // iCloud — fetch calendar list when connected and not yet loaded
  const [icloudListLoading,setIcloudListLoading]=useState(false);
  useEffect(()=>{
    if(icloudCalStatus!=="connected"||icloudListLoading||(icloudSelectedCalIds!==null&&icloudCalendars!==null)) return;
    setIcloudListLoading(true);
    (async()=>{
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(!session?.access_token) return;
        const res=await apiFetch("/api/icloud/calendars",{headers:{Authorization:`Bearer ${session.access_token}`}});
        if(!res.ok) return;
        const json=await res.json();
        setIcloudCalendars(json.calendars||[]);
        setIcloudSelectedCalIds(json.selected_calendar_ids||[]);
      }catch{}
      finally{setIcloudListLoading(false);}
    })();
  },[icloudCalStatus,icloudSelectedCalIds,icloudCalendars]);

  const toggleIcloudCal=async(calId)=>{
    const {data:{session}}=await supabase.auth.getSession();
    if(!session?.access_token) return;
    const next=icloudSelectedCalIds.includes(calId)
      ?icloudSelectedCalIds.filter(id=>id!==calId)
      :[...icloudSelectedCalIds,calId];
    setIcloudSelectedCalIds(next);
    await apiFetch("/api/icloud/calendars",{
      method:"PATCH",
      headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},
      body:JSON.stringify({selected_calendar_ids:next}),
    });
    fetchICloudCalEvents(session.access_token);
  };

  // Outlook — fetch calendar list when connected and not yet loaded
  const [outlookListLoading,setOutlookListLoading]=useState(false);
  useEffect(()=>{
    if(outlookCalStatus!=="connected"||outlookListLoading||(outlookSelectedCalIds!==null&&outlookCalendars!==null)) return;
    setOutlookListLoading(true);
    (async()=>{
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(!session?.access_token) return;
        const res=await apiFetch("/api/outlook/calendars",{headers:{Authorization:`Bearer ${session.access_token}`}});
        if(!res.ok) return;
        const json=await res.json();
        setOutlookCalendars(json.calendars||[]);
        setOutlookSelectedCalIds(json.selected_calendar_ids||[]);
      }catch{}
      finally{setOutlookListLoading(false);}
    })();
  },[outlookCalStatus,outlookSelectedCalIds,outlookCalendars]);

  const toggleOutlookCal=async(calId)=>{
    const {data:{session}}=await supabase.auth.getSession();
    if(!session?.access_token) return;
    const next=outlookSelectedCalIds.includes(calId)
      ?outlookSelectedCalIds.filter(id=>id!==calId)
      :[...outlookSelectedCalIds,calId];
    setOutlookSelectedCalIds(next);
    await apiFetch("/api/outlook/calendars",{
      method:"PATCH",
      headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},
      body:JSON.stringify({selected_calendar_ids:next}),
    });
    fetchOutlookCalEvents(session.access_token);
  };

  // Yahoo — fetch calendar list when connected and not yet loaded
  const [yahooListLoading,setYahooListLoading]=useState(false);
  useEffect(()=>{
    if(yahooCalStatus!=="connected"||yahooListLoading||(yahooSelectedCalIds!==null&&yahooCalendars!==null)) return;
    setYahooListLoading(true);
    (async()=>{
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(!session?.access_token) return;
        const res=await apiFetch("/api/yahoo/calendars",{headers:{Authorization:`Bearer ${session.access_token}`}});
        if(!res.ok) return;
        const json=await res.json();
        setYahooCalendars(json.calendars||[]);
        setYahooSelectedCalIds(json.selected_calendar_ids||[]);
      }catch{}
      finally{setYahooListLoading(false);}
    })();
  },[yahooCalStatus,yahooSelectedCalIds,yahooCalendars]);

  const toggleYahooCal=async(calId)=>{
    const {data:{session}}=await supabase.auth.getSession();
    if(!session?.access_token) return;
    const next=yahooSelectedCalIds.includes(calId)?yahooSelectedCalIds.filter(id=>id!==calId):[...yahooSelectedCalIds,calId];
    setYahooSelectedCalIds(next);
    await apiFetch("/api/yahoo/calendars",{method:"PATCH",headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},body:JSON.stringify({selected_calendar_ids:next})});
    fetchYahooCalEvents(session.access_token);
  };

  // CalDAV — fetch calendar list when connected and not yet loaded
  const [caldavListLoading,setCaldavListLoading]=useState(false);
  useEffect(()=>{
    if(caldavCalStatus!=="connected"||caldavListLoading||(caldavSelectedCalIds!==null&&caldavCalendars!==null)) return;
    setCaldavListLoading(true);
    (async()=>{
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(!session?.access_token) return;
        const res=await apiFetch("/api/caldav/calendars",{headers:{Authorization:`Bearer ${session.access_token}`}});
        if(!res.ok) return;
        const json=await res.json();
        setCaldavCalendars(json.calendars||[]);
        setCaldavSelectedCalIds(json.selected_calendar_ids||[]);
      }catch{}
      finally{setCaldavListLoading(false);}
    })();
  },[caldavCalStatus,caldavSelectedCalIds,caldavCalendars]);

  const toggleCaldavCal=async(calId)=>{
    const {data:{session}}=await supabase.auth.getSession();
    if(!session?.access_token) return;
    const next=caldavSelectedCalIds.includes(calId)?caldavSelectedCalIds.filter(id=>id!==calId):[...caldavSelectedCalIds,calId];
    setCaldavSelectedCalIds(next);
    await apiFetch("/api/caldav/calendars",{method:"PATCH",headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},body:JSON.stringify({selected_calendar_ids:next})});
    fetchCaldavCalEvents(session.access_token);
  };

  // flow: null | "picker" | "form" | "connecting" | "done"
  const [flow,setFlow]=useState(null);
  const [chosenProvider,setChosenProvider]=useState(null);
  const [formEmail,setFormEmail]=useState("");
  const [formPass,setFormPass]=useState("");
  const [formServerUrl,setFormServerUrl]=useState("");
  const [editingCalId,setEditingCalId]=useState(null);
  const [editingCalName,setEditingCalName]=useState("");
  const [expandedAcc,setExpandedAcc]=useState(null);
  // Compute real conflicts by comparing orbit scheduling events (source='orbit')
  // against personal calendar events (source!='orbit', status='busy', not allDay).
  // Overlap: orbit.startH < cal.endH && orbit.endH > cal.startH on the same date.
  const conflicts=useMemo(()=>{
    const orbitEvts=calEvents.filter(e=>e.source==="orbit"&&!e.allDay&&e.date);
    const calEvts=calEvents.filter(e=>e.source!=="orbit"&&!e.allDay&&e.status==="busy");
    const found=[];
    for(const o of orbitEvts){
      for(const c of calEvts){
        if(o.date!==c.date) continue;
        if(o.startH>=c.endH||o.endH<=c.startH) continue;
        const overlapH=Math.min(o.endH,c.endH)-Math.max(o.startH,c.startH);
        found.push({
          id:`conflict-${o.id}-${c.id}`,
          sev:overlapH>=1?"high":"medium",
          orbitEvent:o,
          calEvent:c,
        });
      }
    }
    return found;
  },[calEvents]);
  const [resolved,setResolved]=useState(()=>{try{const v=localStorage.getItem("orbit_conflicts_resolved");return v?JSON.parse(v):[];}catch{return[];}});
  useEffect(()=>{try{localStorage.setItem("orbit_conflicts_resolved",JSON.stringify(resolved));}catch{}},[resolved]);
  const [tab,setTab]=useState("accounts");
  const openConflicts=conflicts.filter(c=>!resolved.includes(c.id));

  const startAdd=()=>{setChosenProvider(null);setFormEmail("");setFormPass("");setFormServerUrl("");setFlow("picker");};
  const pickProvider=async(p)=>{
    if(p.id==="google"&&userId){
      const {data:{session}}=await supabase.auth.getSession();
      if(!session?.access_token)return;
      try{
        if(isNative()){
          const providerUrl=await fetchOAuthUrl('/api/google/auth',session.access_token,{native:true});
          const result=await openNativeOAuth(providerUrl);
          if(result.status==="connected"){setGoogleCalStatus("loading");fetchGoogleCalEvents();}
          else if(result.error){setGoogleCalStatus("error");}
        }else{
          const providerUrl=await fetchOAuthUrl('/api/google/auth',session.access_token,{popup:true});
          openOAuthPopup(providerUrl,{
            onSuccess:()=>{setGoogleCalStatus("loading");fetchGoogleCalEvents();},
            onError:()=>setGoogleCalStatus("error"),
          });
        }
      }catch(e){console.error('[google-connect]',e?.message||e);setGoogleCalStatus("error");}
      return;
    }
    if(p.id==="outlook"&&userId){
      const {data:{session}}=await supabase.auth.getSession();
      if(!session?.access_token)return;
      try{
        if(isNative()){
          const providerUrl=await fetchOAuthUrl('/api/outlook/auth',session.access_token,{native:true});
          const result=await openNativeOAuth(providerUrl);
          if(result.status==="connected"){setOutlookCalStatus("loading");fetchOutlookCalEvents();}
          else if(result.error){setOutlookCalStatus("error");}
        }else{
          const providerUrl=await fetchOAuthUrl('/api/outlook/auth',session.access_token,{popup:true});
          openOAuthPopup(providerUrl,{
            onSuccess:()=>{setOutlookCalStatus("loading");fetchOutlookCalEvents();},
            onError:()=>setOutlookCalStatus("error"),
          });
        }
      }catch(e){console.error('[outlook-connect]',e?.message||e);setOutlookCalStatus("error");}
      return;
    }
    setChosenProvider(p);setFlow("form");
  };
  const [formError,setFormError]=useState("");
  const [ofwFileContent,setOfwFileContent]=useState(""); // raw .ics text read by FileReader
  const submitForm=async()=>{
    if(chosenProvider?.id!=="ofw"&&!formEmail.trim())return;
    if(chosenProvider?.id==="ofw"&&!ofwFileContent.trim())return;
    setFormError("");
    setFlow("connecting");
    // iCloud → real CalDAV connection
    if(chosenProvider?.id==="icloud"){
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(!session?.access_token){setFormError("Not signed in");setFlow("form");return;}
        const res=await apiFetch("/api/icloud/connect",{
          method:"POST",
          headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},
          body:JSON.stringify({apple_id:formEmail.trim(),app_password:formPass.trim()}),
        });
        const json=await res.json().catch(()=>({}));
        if(!res.ok){
          if(res.status===494){
            // JWT too large — strip profile photo from metadata, refresh session, then retry
            setFormError("Fixing oversized session token…");
            await supabase.auth.updateUser({data:{avatar_url:null,picture:null}});
            const {data:{session:newSession},error:refreshErr}=await supabase.auth.refreshSession();
            if(refreshErr||!newSession?.access_token){
              setFormError("Session could not be refreshed. Please sign out and sign back in.");
              setFlow("form");return;
            }
            const retry=await apiFetch("/api/icloud/connect",{
              method:"POST",
              headers:{Authorization:`Bearer ${newSession.access_token}`,"Content-Type":"application/json"},
              body:JSON.stringify({apple_id:formEmail.trim(),app_password:formPass.trim()}),
            });
            const retryJson=await retry.json().catch(()=>({}));
            if(!retry.ok){
              setFormError(retryJson.error==="invalid_credentials"
                ?"Incorrect Apple ID or app-specific password. Make sure you're using an app-specific password (not your regular Apple ID password) from appleid.apple.com."
                :(retryJson.message||retryJson.error||`Connection failed (HTTP ${retry.status})`));
              setFlow("form");return;
            }
            setIcloudCalStatus("connected");
            setIcloudCalendars(retryJson.calendars||[]);
            setIcloudSelectedCalIds(retryJson.selected_calendar_ids||[]);
            await fetchICloudCalEvents(newSession.access_token);
            setFlow("done");
            setTimeout(()=>setFlow(null),1600);
            return;
          }
          const msg=json.error==="invalid_credentials"
            ?"Incorrect Apple ID or app-specific password. Make sure you're using an app-specific password (not your regular Apple ID password) from appleid.apple.com."
            :(json.message||json.error||`Connection failed (HTTP ${res.status})`);
          setFormError(msg);setFlow("form");return;
        }
        setIcloudCalStatus("connected");
        setIcloudCalendars(json.calendars||[]);
        setIcloudSelectedCalIds(json.selected_calendar_ids||[]);
        // Fetch events immediately
        await fetchICloudCalEvents(session.access_token);
        setFlow("done");
        setTimeout(()=>setFlow(null),1600);
      }catch(e){
        setFormError(e?.message||"Connection failed");setFlow("form");
      }
      return;
    }
    // OFW — upload .ics file content
    if(chosenProvider?.id==="ofw"){
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(!session?.access_token){setFormError("Not signed in");setFlow("form");return;}
        const res=await apiFetch("/api/ofw/connect",{
          method:"POST",
          headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},
          body:JSON.stringify({ics_content:ofwFileContent.trim()}),
        });
        const json=await res.json().catch(()=>({}));
        if(!res.ok){
          const baseMsg=json.message||json.error||`Import failed (HTTP ${res.status})`;
          setFormError(json.detail?`${baseMsg}: ${json.detail}`:baseMsg);
          setFlow("form");return;
        }
        setOfwFileContent("");
        setOfwCalStatus("connected");
        setOfwUploadedAt(new Date().toISOString());
        await fetchOfwCalEvents(session.access_token);
        setFlow("done");
        setTimeout(()=>setFlow(null),1600);
      }catch(e){
        setFormError(e?.message||"Import failed");setFlow("form");
      }
      return;
    }
    // Yahoo — real CalDAV connection
    if(chosenProvider?.id==="yahoo"){
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(!session?.access_token){setFormError("Not signed in");setFlow("form");return;}
        const res=await apiFetch("/api/yahoo/connect",{
          method:"POST",
          headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},
          body:JSON.stringify({username:formEmail.trim(),password:formPass.trim()}),
        });
        const json=await res.json().catch(()=>({}));
        if(!res.ok){setFormError(json.message||json.error||"Connection failed");setFlow("form");return;}
        setYahooCalendars(json.calendars||[]);
        setYahooSelectedCalIds(json.selected_calendar_ids||[]);
        setYahooCalStatus("connected");
        await fetchYahooCalEvents(session.access_token);
        setFlow("done");
        setTimeout(()=>setFlow(null),1600);
      }catch(e){setFormError(e?.message||"Connection failed");setFlow("form");}
      return;
    }
    // Generic CalDAV — real connection
    if(chosenProvider?.id==="caldav"){
      if(!formServerUrl.trim()){setFormError("Server URL is required");setFlow("form");return;}
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(!session?.access_token){setFormError("Not signed in");setFlow("form");return;}
        const res=await apiFetch("/api/caldav/connect",{
          method:"POST",
          headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},
          body:JSON.stringify({server_url:formServerUrl.trim(),username:formEmail.trim(),password:formPass.trim()}),
        });
        const json=await res.json().catch(()=>({}));
        if(!res.ok){setFormError(json.message||json.error||"Connection failed");setFlow("form");return;}
        setCaldavCalendars(json.calendars||[]);
        setCaldavSelectedCalIds(json.selected_calendar_ids||[]);
        setCaldavCalStatus("connected");
        await fetchCaldavCalEvents(session.access_token);
        setFlow("done");
        setTimeout(()=>setFlow(null),1600);
      }catch(e){setFormError(e?.message||"Connection failed");setFlow("form");}
      return;
    }
    // Other providers — mock flow
    setTimeout(()=>{
      const prov=chosenProvider;
      const newAcc={
        id:`acc-${Date.now()}`,
        providerId:prov.id,
        email:formEmail,
        label:prov.label,
        calendars:prov.calendars.map((name,i)=>({
          id:`cal-${Date.now()}-${i}`,
          name,
          color:["#1C7BE0","#34A853","#FF9500","#FF3B30","#AF52DE","#FF2D55"][i%6],
          enabled:i===0,
        })),
      };
      setAccounts(a=>[...a,newAcc]);
      setFlow("done");
      setTimeout(()=>setFlow(null),1600);
    },2000);
  };
  const removeAccount=(id)=>setAccounts(a=>a.filter(x=>x.id!==id));
  const toggleCal=(accId,calId)=>setAccounts(a=>a.map(acc=>acc.id!==accId?acc:{...acc,calendars:acc.calendars.map(c=>c.id!==calId?c:{...c,enabled:!c.enabled})}));

  const inputStyle={width:"100%",padding:"10px 12px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.panel,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box",marginBottom:10};

  return(<div>
    {/* Tab bar */}
    <div style={{display:"flex",gap:1,marginBottom:16,borderBottom:`1px solid ${T.edge}`}}>
      {[{id:"accounts",l:"Accounts"},{id:"conflicts",l:`Conflicts${openConflicts.length?` (${openConflicts.length})`:""}`}].map(t=>(
        <button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"6px 14px",border:"none",cursor:"pointer",background:"transparent",color:tab===t.id?T.ink:T.muted,fontSize:12,fontWeight:tab===t.id?600:400,borderBottom:tab===t.id?`2px solid ${T.clay}`:"2px solid transparent",marginBottom:-1}}>
          {t.id==="conflicts"&&openConflicts.length?<span style={{color:T.red}}>{t.l}</span>:t.l}
        </button>
      ))}
    </div>

    {/* Accounts tab */}
    {tab==="accounts"&&<>
      <CalConnectErrorBanner error={googleOAuthError}  calStatus={googleCalStatus}  title="Google Calendar connection failed" hint="This can happen if you denied access, or if the app is awaiting Google OAuth approval. Try connecting again."/>
      <CalConnectErrorBanner error={outlookOAuthError} calStatus={outlookCalStatus} title="Outlook connection failed"           hint="Check that your redirect URI in Azure matches exactly and that the outlook_tokens table has been created in Supabase."/>
      <CalConnectErrorBanner error={yahooOAuthError}   calStatus={yahooCalStatus}   title="Yahoo Calendar connection failed"   hint="Check redirect URI and confirm the SQL migration was run in Supabase."/>
      <CalConnectErrorBanner error={caldavConnectError} calStatus={caldavCalStatus}  title="CalDAV connection failed"           hint="Check your server URL and credentials, and confirm the SQL migration was run in Supabase."/>
      {/* Google Calendar — multi-account, one card per connected account */}
      {googleCalStatus==="connected"&&(googleAccounts||[]).map(acc=>{const gKey=`google-${acc.email}`;const gCollapsed=!expandedProviders.has(gKey);return(
        <div key={acc.email} style={{background:T.surface,borderRadius:14,marginBottom:10,border:`1px solid ${acc.status==="connected"?"#1A73E840":acc.status==="needs_reauth"?T.red+"40":T.edge}`,overflow:"hidden"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",cursor:"pointer"}} onClick={()=>toggleCollapse(gKey)}>
            <span style={{fontSize:22,flexShrink:0}}>🗓️</span>
            <div style={{flex:1,minWidth:0}}>
              <p style={{fontSize:14,fontWeight:600,color:T.ink}}>Google Calendar</p>
              <p style={{fontSize:11,color:acc.status==="connected"?"#1A73E8":T.red,marginTop:2}}>
                {acc.email||"(no email)"}
                {acc.status==="connected"&&` · ${fmtSynced(syncedAt.google)}`}
                {acc.status==="needs_reauth"&&" · Session expired"}
                {acc.status==="error"&&" · Connection error"}
                {acc.calendars?.length>0&&<span style={{color:T.muted}}> · {acc.selectedCalIds?.length||0}/{acc.calendars.length} calendars</span>}
              </p>
            </div>
            <span style={{fontSize:16,color:T.bark,transition:"transform .2s",display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:6,background:T.mist,border:`1px solid ${T.edge}`,transform:gCollapsed?"rotate(0deg)":"rotate(90deg)",marginRight:8,flexShrink:0}}>›</span>
            <button onClick={async(e)=>{e.stopPropagation();
              const {data:{session}}=await supabase.auth.getSession();
              if(!session) return;
              await apiFetch(`/api/google/disconnect?account_email=${encodeURIComponent(acc.email)}`,{method:"DELETE",headers:{Authorization:`Bearer ${session.access_token}`}});
              const remaining=(googleAccounts||[]).filter(a=>a.email!==acc.email);
              setGoogleAccounts(remaining);
              if(remaining.length===0){
                setGoogleCalAvail(null);
                setGoogleCalStatus(null);
                setCalEvents(prev=>prev.filter(e=>e.source!=="google"));
              } else {
                // Refresh events without the disconnected account
                fetchGoogleCalEvents(session.access_token);
              }
            }} style={{fontSize:11,color:T.red,background:"none",border:"none",cursor:"pointer",padding:0,flexShrink:0}}>Disconnect</button>
          </div>
          {/* Calendar list — collapsible */}
          {!gCollapsed&&<div style={{borderTop:`1px solid ${T.edge}`,background:T.panel}}>
            {calListLoading&&!acc.calendars?.length&&<p style={{fontSize:11,color:T.muted,padding:"10px 16px"}}>Loading calendars…</p>}
            {acc.calendars?.map((cal,i)=>{
              const sel=acc.selectedCalIds?.includes(cal.id)??cal.primary;
              const calColor=cal.backgroundColor||"#1A73E8";
              const calName=calendarCustomNames[cal.id]||cal.summary;
              return(<div key={cal.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",borderBottom:i<acc.calendars.length-1?`1px solid ${T.edge}`:"none"}}>
                <div style={{width:12,height:12,borderRadius:3,background:calColor,flexShrink:0}}/>
                <div style={{flex:1,minWidth:0,display:"flex",alignItems:"center",gap:4}}>
                  {editingCalId===cal.id
                    ?<input autoFocus value={editingCalName} onChange={e=>setEditingCalName(e.target.value)} onBlur={()=>{setCalendarCustomNames(n=>({...n,[cal.id]:editingCalName}));setEditingCalId(null);}} onKeyDown={e=>{if(e.key==="Enter"){setCalendarCustomNames(n=>({...n,[cal.id]:editingCalName}));setEditingCalId(null);}if(e.key==="Escape")setEditingCalId(null);}} style={{flex:1,fontSize:13,color:T.ink,background:"none",border:"none",borderBottom:`1px solid ${T.clay}`,outline:"none",padding:"0 2px"}}/>
                    :<span onClick={()=>{setEditingCalId(cal.id);setEditingCalName(calName);}} style={{fontSize:13,color:T.ink,cursor:"text"}} title="Click to rename">{calName}</span>}
                  {cal.primary&&<span style={{fontSize:11,color:T.muted,marginLeft:4}}>primary</span>}
                </div>
                <div onClick={()=>toggleGoogleCal(acc.email,cal.id)} style={{width:42,height:24,borderRadius:12,background:sel?"#1A73E8":T.edge,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:sel?21:3,width:18,height:18,borderRadius:"50%",background:"white",boxShadow:"0 1px 3px rgba(0,0,0,.25)",transition:"left .2s"}}/>
                </div>
              </div>);
            })}
          </div>}
        </div>
      );})}
      {/* Google Calendar connect row — only shown when not yet connected (or error/reauth).
          When connected, additional accounts are added via the "Add Account" picker below. */}
      {userId&&googleCalStatus!=="connected"&&(
        <div style={{background:T.surface,borderRadius:14,marginBottom:10,border:`1px solid ${googleCalStatus==="needs_reauth"||googleCalStatus==="error"?T.red+"40":T.edge}`,overflow:"hidden"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px"}}>
            <span style={{fontSize:22,flexShrink:0}}>🗓️</span>
            <div style={{flex:1,minWidth:0}}>
              <p style={{fontSize:14,fontWeight:600,color:T.ink}}>Google Calendar</p>
              {googleCalStatus==="loading"&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Connecting…</p>}
              {(googleCalStatus===null||!googleCalStatus)&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Not connected</p>}
              {googleCalStatus==="error"&&<p style={{fontSize:11,color:T.red,marginTop:2}}>Connection error — try again</p>}
              {googleCalStatus==="needs_reauth"&&<p style={{fontSize:11,color:T.red,marginTop:2}}>Session expired — reconnect</p>}
            </div>
            {googleCalStatus==="loading"
              ?<div style={{width:7,height:7,borderRadius:"50%",background:T.muted,animation:"pulse 1.5s infinite",flexShrink:0}}/>
              :<button onClick={async()=>{try{if(!userId)return;const {data:{session}}=await supabase.auth.getSession();if(!session?.access_token)return;const providerUrl=await fetchOAuthUrl('/api/google/auth',session.access_token,{native:isNative()});if(isNative()){const r=await openNativeOAuth(providerUrl);if(r.status==="connected"){setGoogleCalStatus("loading");fetchGoogleCalEvents();}else if(r.error){setGoogleCalStatus("error");}}else{window.location.href=providerUrl;}}catch(e){console.error('[google-connect]',e?.message||e);setGoogleCalStatus("error");}}} style={{fontSize:11,color:googleCalStatus==="error"||googleCalStatus==="needs_reauth"?T.red:"#1A73E8",fontWeight:600,flexShrink:0,background:"none",border:"none",cursor:"pointer",padding:0}}>
                {googleCalStatus==="needs_reauth"?"Reconnect":googleCalStatus==="error"?"Retry":"Connect"}
              </button>
            }
          </div>
        </div>
      )}

      {/* iCloud Calendar — real CalDAV integration */}
      {(()=>{const icCollapsed=!expandedProviders.has("icloud");return(
      <div style={{background:T.surface,borderRadius:14,marginBottom:10,border:`1px solid ${icloudCalStatus==="connected"?"#1C7BE040":icloudCalStatus==="error"?T.red+"40":T.edge}`,overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",cursor:icloudCalStatus==="connected"?"pointer":"default"}} onClick={()=>icloudCalStatus==="connected"&&toggleCollapse("icloud")}>
          <span style={{fontSize:22,flexShrink:0}}>☁️</span>
          <div style={{flex:1,minWidth:0}}>
            <p style={{fontSize:14,fontWeight:600,color:T.ink}}>iCloud Calendar</p>
            {icloudCalStatus==="connected"&&<p style={{fontSize:11,color:"#1C7BE0",marginTop:2}}>Connected · {fmtSynced(syncedAt.icloud)}{icloudCalendars?.length>0&&<span style={{color:T.muted}}> · {icloudSelectedCalIds?.length||0}/{icloudCalendars.length} calendars</span>}</p>}
            {icloudCalStatus==="loading"&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Connecting…</p>}
            {icloudCalStatus==="error"&&<p style={{fontSize:11,color:T.red,marginTop:2}}>Connection error</p>}
            {(icloudCalStatus===null||!icloudCalStatus)&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Not connected</p>}
          </div>
          {icloudCalStatus==="connected"&&<span style={{fontSize:16,color:T.bark,transition:"transform .2s",display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:6,background:T.mist,border:`1px solid ${T.edge}`,transform:icCollapsed?"rotate(0deg)":"rotate(90deg)",marginRight:8,flexShrink:0}}>›</span>}
          {icloudCalStatus==="connected"&&(
            <button onClick={async(e)=>{e.stopPropagation();
              const {data:{session}}=await supabase.auth.getSession();
              if(!session) return;
              await apiFetch("/api/icloud/disconnect",{method:"DELETE",headers:{Authorization:`Bearer ${session.access_token}`}});
              setIcloudCalStatus(null);
              setIcloudCalendars(null);
              setIcloudSelectedCalIds(null);
              setCalEvents(prev=>prev.filter(e=>e.source!=="icloud"));
            }} style={{fontSize:11,color:T.red,background:"none",border:"none",cursor:"pointer",padding:0,flexShrink:0}}>Disconnect</button>
          )}
          {(icloudCalStatus===null||!icloudCalStatus||icloudCalStatus==="error")&&(
            <button onClick={(e)=>{e.stopPropagation();setChosenProvider(CAL_PROVIDERS.find(p=>p.id==="icloud"));setFormEmail("");setFormPass("");setFormError("");setFlow("form");}} style={{fontSize:11,color:"#1C7BE0",background:"none",border:"none",cursor:"pointer",padding:0,fontWeight:600,flexShrink:0}}>Connect</button>
          )}
          {icloudCalStatus==="loading"&&(
            <div style={{width:7,height:7,borderRadius:"50%",background:T.muted,animation:"pulse 1.5s infinite",flexShrink:0}}/>
          )}
        </div>
        {/* Calendar list — collapsible */}
        {icloudCalStatus==="connected"&&!icCollapsed&&(
          <div style={{borderTop:`1px solid ${T.edge}`,background:T.panel}}>
            {icloudListLoading&&<p style={{fontSize:11,color:T.muted,padding:"10px 16px"}}>Loading calendars…</p>}
            {!icloudListLoading&&icloudCalendars&&icloudCalendars.map((cal,i)=>{
              const sel=icloudSelectedCalIds?.includes(cal.id)??true;
              const calName=calendarCustomNames[cal.id]||cal.name;
              return(<div key={cal.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",borderBottom:i<icloudCalendars.length-1?`1px solid ${T.edge}`:"none"}}>
                <div style={{width:12,height:12,borderRadius:3,background:cal.color||"#1C7BE0",flexShrink:0}}/>
                {editingCalId===cal.id
                  ?<input autoFocus value={editingCalName} onChange={e=>setEditingCalName(e.target.value)} onBlur={()=>{setCalendarCustomNames(n=>({...n,[cal.id]:editingCalName}));setEditingCalId(null);}} onKeyDown={e=>{if(e.key==="Enter"){setCalendarCustomNames(n=>({...n,[cal.id]:editingCalName}));setEditingCalId(null);}if(e.key==="Escape")setEditingCalId(null);}} style={{flex:1,fontSize:13,color:T.ink,background:"none",border:"none",borderBottom:`1px solid ${T.clay}`,outline:"none",padding:"0 2px"}}/>
                  :<span onClick={()=>{setEditingCalId(cal.id);setEditingCalName(calName);}} style={{flex:1,fontSize:13,color:T.ink,cursor:"text"}} title="Click to rename">{calName}</span>}
                <div onClick={()=>toggleIcloudCal(cal.id)} style={{width:42,height:24,borderRadius:12,background:sel?"#1C7BE0":T.edge,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:sel?21:3,width:18,height:18,borderRadius:"50%",background:"white",boxShadow:"0 1px 3px rgba(0,0,0,.25)",transition:"left .2s"}}/>
                </div>
              </div>);
            })}
          </div>
        )}
      </div>);})()}

      {/* Yahoo! Calendar — real CalDAV integration */}
      {(()=>{const yhCollapsed=!expandedProviders.has("yahoo");return(
      <div style={{background:T.surface,borderRadius:14,marginBottom:10,border:`1px solid ${yahooCalStatus==="connected"?"#6001D240":yahooCalStatus==="error"?T.red+"40":T.edge}`,overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",cursor:yahooCalStatus==="connected"?"pointer":"default"}} onClick={()=>yahooCalStatus==="connected"&&toggleCollapse("yahoo")}>
          <span style={{fontSize:22,flexShrink:0}}>💜</span>
          <div style={{flex:1,minWidth:0}}>
            <p style={{fontSize:14,fontWeight:600,color:T.ink}}>Yahoo! Calendar</p>
            {yahooCalStatus==="connected"&&<p style={{fontSize:11,color:"#6001D2",marginTop:2}}>Connected · {fmtSynced(syncedAt.yahoo)}{yahooCalendars?.length>0&&<span style={{color:T.muted}}> · {yahooSelectedCalIds?.length||0}/{yahooCalendars.length} calendars</span>}</p>}
            {yahooCalStatus==="loading"&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Connecting…</p>}
            {yahooCalStatus==="error"&&<p style={{fontSize:11,color:T.red,marginTop:2}}>Connection error</p>}
            {(yahooCalStatus===null||!yahooCalStatus)&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Not connected</p>}
          </div>
          {yahooCalStatus==="connected"&&<span style={{fontSize:16,color:T.bark,transition:"transform .2s",display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:6,background:T.mist,border:`1px solid ${T.edge}`,transform:yhCollapsed?"rotate(0deg)":"rotate(90deg)",marginRight:8,flexShrink:0}}>›</span>}
          {yahooCalStatus==="connected"&&(
            <button onClick={async(e)=>{e.stopPropagation();
              try{
                const {data:{session}}=await supabase.auth.getSession();
                if(!session) return;
                const res=await apiFetch("/api/yahoo/disconnect",{method:"DELETE",headers:{Authorization:`Bearer ${session.access_token}`}});
                if(!res.ok) return;
                setYahooCalStatus(null);
                setYahooCalendars(null);
                setYahooSelectedCalIds(null);
                setCalEvents(prev=>prev.filter(e=>e.source!=="yahoo"));
              }catch(_){}
            }} style={{fontSize:11,color:T.red,background:"none",border:"none",cursor:"pointer",padding:0,flexShrink:0}}>Disconnect</button>
          )}
          {(yahooCalStatus===null||!yahooCalStatus||yahooCalStatus==="error")&&(
            <button onClick={(e)=>{e.stopPropagation();setChosenProvider(CAL_PROVIDERS.find(p=>p.id==="yahoo"));setFormEmail("");setFormPass("");setFormError("");setFlow("form");}} style={{fontSize:11,color:"#6001D2",background:"none",border:"none",cursor:"pointer",padding:0,fontWeight:600,flexShrink:0}}>Connect</button>
          )}
          {yahooCalStatus==="loading"&&(
            <div style={{width:7,height:7,borderRadius:"50%",background:T.muted,animation:"pulse 1.5s infinite",flexShrink:0}}/>
          )}
        </div>
        {yahooCalStatus==="connected"&&!yhCollapsed&&(
          <div style={{borderTop:`1px solid ${T.edge}`,background:T.panel}}>
            {yahooListLoading&&<p style={{fontSize:11,color:T.muted,padding:"10px 16px"}}>Loading calendars…</p>}
            {!yahooListLoading&&yahooCalendars&&yahooCalendars.map((cal,i)=>{
              const sel=yahooSelectedCalIds?.includes(cal.id)??true;
              const calName=calendarCustomNames[cal.id]||cal.name;
              return(<div key={cal.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",borderBottom:i<yahooCalendars.length-1?`1px solid ${T.edge}`:"none"}}>
                <div style={{width:12,height:12,borderRadius:3,background:cal.color||"#6001D2",flexShrink:0}}/>
                {editingCalId===cal.id
                  ?<input autoFocus value={editingCalName} onChange={e=>setEditingCalName(e.target.value)} onBlur={()=>{setCalendarCustomNames(n=>({...n,[cal.id]:editingCalName}));setEditingCalId(null);}} onKeyDown={e=>{if(e.key==="Enter"){setCalendarCustomNames(n=>({...n,[cal.id]:editingCalName}));setEditingCalId(null);}if(e.key==="Escape")setEditingCalId(null);}} style={{flex:1,fontSize:13,color:T.ink,background:"none",border:"none",borderBottom:`1px solid ${T.clay}`,outline:"none",padding:"0 2px"}}/>
                  :<span onClick={()=>{setEditingCalId(cal.id);setEditingCalName(calName);}} style={{flex:1,fontSize:13,color:T.ink,cursor:"text"}} title="Click to rename">{calName}</span>}
                <div onClick={()=>toggleYahooCal(cal.id)} style={{width:42,height:24,borderRadius:12,background:sel?"#6001D2":T.edge,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:sel?21:3,width:18,height:18,borderRadius:"50%",background:"white",boxShadow:"0 1px 3px rgba(0,0,0,.25)",transition:"left .2s"}}/>
                </div>
              </div>);
            })}
          </div>
        )}
      </div>);})()}

      {/* Outlook / Exchange — real OAuth integration */}
      {(()=>{const olCollapsed=!expandedProviders.has("outlook");return(
      <div style={{background:T.surface,borderRadius:14,marginBottom:10,border:`1px solid ${outlookCalStatus==="connected"?"#0078D440":outlookCalStatus==="needs_reauth"?T.red+"40":outlookCalStatus==="error"?T.red+"40":T.edge}`,overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",cursor:outlookCalStatus==="connected"?"pointer":"default"}} onClick={()=>outlookCalStatus==="connected"&&toggleCollapse("outlook")}>
          <span style={{fontSize:22,flexShrink:0}}>📆</span>
          <div style={{flex:1,minWidth:0}}>
            <p style={{fontSize:14,fontWeight:600,color:T.ink}}>Outlook / Exchange</p>
            {outlookCalStatus==="connected"&&<p style={{fontSize:11,color:"#0078D4",marginTop:2}}>Connected · {fmtSynced(syncedAt.outlook)}{outlookCalendars?.length>0&&<span style={{color:T.muted}}> · {outlookSelectedCalIds?.length||0}/{outlookCalendars.length} calendars</span>}</p>}
            {outlookCalStatus==="loading"&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Connecting…</p>}
            {outlookCalStatus==="error"&&<p style={{fontSize:11,color:T.red,marginTop:2}}>Connection error — tap Retry</p>}
            {outlookCalStatus==="needs_reauth"&&<p style={{fontSize:11,color:T.red,marginTop:2}}>Session expired — reconnect to continue syncing</p>}
            {(outlookCalStatus===null||!outlookCalStatus)&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Not connected</p>}
          </div>
          {outlookCalStatus==="connected"&&<span style={{fontSize:16,color:T.bark,transition:"transform .2s",display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:6,background:T.mist,border:`1px solid ${T.edge}`,transform:olCollapsed?"rotate(0deg)":"rotate(90deg)",marginRight:8,flexShrink:0}}>›</span>}
          {outlookCalStatus==="connected"&&(
            <button onClick={async(e)=>{e.stopPropagation();
              const {data:{session}}=await supabase.auth.getSession();
              if(!session) return;
              await apiFetch("/api/outlook/disconnect",{method:"DELETE",headers:{Authorization:`Bearer ${session.access_token}`}});
              setOutlookCalStatus(null);
              setOutlookCalendars(null);
              setOutlookSelectedCalIds(null);
              setCalEvents(prev=>prev.filter(e=>e.source!=="outlook"));
            }} style={{fontSize:11,color:T.red,background:"none",border:"none",cursor:"pointer",padding:0,flexShrink:0}}>Disconnect</button>
          )}
          {(outlookCalStatus===null||!outlookCalStatus||outlookCalStatus==="error"||outlookCalStatus==="needs_reauth")&&userId&&(
            <button onClick={async(e)=>{e.stopPropagation();try{if(!userId)return;const {data:{session}}=await supabase.auth.getSession();if(!session?.access_token)return;const providerUrl=await fetchOAuthUrl('/api/outlook/auth',session.access_token,{native:isNative()});if(isNative()){const r=await openNativeOAuth(providerUrl);if(r.status==="connected"){setOutlookCalStatus("loading");fetchOutlookCalEvents();}else if(r.error){setOutlookCalStatus("error");}}else{window.location.href=providerUrl;}}catch(e){console.error('[outlook-connect]',e?.message||e);setOutlookCalStatus("error");}}} style={{fontSize:11,color:outlookCalStatus==="needs_reauth"||outlookCalStatus==="error"?T.red:"#0078D4",fontWeight:600,flexShrink:0,background:"none",border:"none",cursor:"pointer",padding:0}}>
              {outlookCalStatus==="needs_reauth"?"Reconnect":outlookCalStatus==="error"?"Retry":"Connect"}
            </button>
          )}
          {outlookCalStatus==="loading"&&(
            <div style={{width:7,height:7,borderRadius:"50%",background:T.muted,animation:"pulse 1.5s infinite",flexShrink:0}}/>
          )}
        </div>
        {/* Calendar list — collapsible */}
        {outlookCalStatus==="connected"&&!olCollapsed&&(
          <div style={{borderTop:`1px solid ${T.edge}`,background:T.panel}}>
            {outlookListLoading&&<p style={{fontSize:11,color:T.muted,padding:"10px 16px"}}>Loading calendars…</p>}
            {!outlookListLoading&&outlookCalendars&&outlookCalendars.map((cal,i)=>{
              const sel=outlookSelectedCalIds?.includes(cal.id)??cal.isDefault;
              const calName=calendarCustomNames[cal.id]||cal.name;
              return(<div key={cal.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",borderBottom:i<outlookCalendars.length-1?`1px solid ${T.edge}`:"none"}}>
                <div style={{width:12,height:12,borderRadius:3,background:cal.color||"#0078D4",flexShrink:0}}/>
                <div style={{flex:1,minWidth:0,display:"flex",alignItems:"center",gap:4}}>
                  {editingCalId===cal.id
                    ?<input autoFocus value={editingCalName} onChange={e=>setEditingCalName(e.target.value)} onBlur={()=>{setCalendarCustomNames(n=>({...n,[cal.id]:editingCalName}));setEditingCalId(null);}} onKeyDown={e=>{if(e.key==="Enter"){setCalendarCustomNames(n=>({...n,[cal.id]:editingCalName}));setEditingCalId(null);}if(e.key==="Escape")setEditingCalId(null);}} style={{flex:1,fontSize:13,color:T.ink,background:"none",border:"none",borderBottom:`1px solid ${T.clay}`,outline:"none",padding:"0 2px"}}/>
                    :<span onClick={()=>{setEditingCalId(cal.id);setEditingCalName(calName);}} style={{fontSize:13,color:T.ink,cursor:"text"}} title="Click to rename">{calName}</span>}
                  {cal.isDefault&&<span style={{fontSize:11,color:T.muted,marginLeft:4}}>default</span>}
                </div>
                <div onClick={()=>toggleOutlookCal(cal.id)} style={{width:42,height:24,borderRadius:12,background:sel?"#0078D4":T.edge,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:sel?21:3,width:18,height:18,borderRadius:"50%",background:"white",boxShadow:"0 1px 3px rgba(0,0,0,.25)",transition:"left .2s"}}/>
                </div>
              </div>);
            })}
          </div>
        )}
      </div>);})()}

      {/* Our Family Wizard — ICS feed integration */}
      {prefs?.coparentCal===false&&<div style={{background:T.mist,borderRadius:14,marginBottom:10,border:`1px solid ${T.edge}`,padding:"14px 16px",display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:22}}>👶</span>
        <p style={{fontSize:13,color:T.muted}}>Our Family Wizard is disabled — enable <strong>Coparent calendar</strong> in Settings → Preferences.</p>
      </div>}
      {prefs?.coparentCal!==false&&<div style={{background:T.surface,borderRadius:14,marginBottom:10,border:`1px solid ${ofwCalStatus==="connected"?T.coparent+"40":ofwCalStatus==="error"?T.red+"40":T.edge}`,overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px"}}>
          <span style={{fontSize:22,flexShrink:0}}>👶</span>
          <div style={{flex:1,minWidth:0}}>
            <p style={{fontSize:14,fontWeight:600,color:T.ink}}>Our Family Wizard</p>
            {ofwCalStatus==="connected"&&<p style={{fontSize:11,color:T.coparent,marginTop:2}}>Connected</p>}
            {ofwCalStatus==="connected"&&ofwUploadedAt&&(()=>{
              const days=Math.floor((Date.now()-new Date(ofwUploadedAt).getTime())/86400000);
              const label=days===0?"today":days===1?"yesterday":`${days}d ago`;
              const stale=days>=14;
              return<p style={{fontSize:11,color:stale?T.amber:T.muted,marginTop:1}}>
                Imported {label}{stale?" · consider re-importing":""}
              </p>;
            })()}
            {ofwCalStatus==="loading"&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Connecting…</p>}
            {ofwCalStatus==="error"&&<p style={{fontSize:11,color:T.red,marginTop:2}}>Connection error</p>}
            {(ofwCalStatus===null||!ofwCalStatus)&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Not connected</p>}
          </div>
          {ofwCalStatus==="connected"&&(
            <div style={{display:"flex",gap:10,flexShrink:0}}>
              <button onClick={()=>{setChosenProvider(CAL_PROVIDERS.find(p=>p.id==="ofw"));setOfwFileContent("");setFormError("");setFlow("form");}} style={{fontSize:11,color:T.coparent,background:"none",border:"none",cursor:"pointer",padding:0,fontWeight:600}}>Re-import</button>
              <button onClick={async()=>{
                try{
                  const {data:{session}}=await supabase.auth.getSession();
                  if(!session) return;
                  const res=await apiFetch("/api/ofw/disconnect",{method:"DELETE",headers:{Authorization:`Bearer ${session.access_token}`}});
                  if(!res.ok) return; // Keep UI connected — server still has the data
                  setOfwCalStatus(null);
                  setOfwUploadedAt(null);
                  setCalEvents(prev=>prev.filter(e=>e.source!=="ofw"));
                }catch(_){}
              }} style={{fontSize:11,color:T.red,background:"none",border:"none",cursor:"pointer",padding:0}}>Disconnect</button>
            </div>
          )}
          {(ofwCalStatus===null||!ofwCalStatus||ofwCalStatus==="error")&&(
            <button onClick={()=>{setChosenProvider(CAL_PROVIDERS.find(p=>p.id==="ofw"));setOfwFileContent("");setFormError("");setFlow("form");}} style={{fontSize:11,color:T.coparent,background:"none",border:"none",cursor:"pointer",padding:0,fontWeight:600,flexShrink:0}}>Connect</button>
          )}
          {ofwCalStatus==="loading"&&(
            <div style={{width:7,height:7,borderRadius:"50%",background:T.muted,animation:"pulse 1.5s infinite",flexShrink:0}}/>
          )}
        </div>
      </div>}

      {/* Generic CalDAV — form-based integration; only shown once a connection exists or is in progress */}
      {(caldavCalStatus==="connected"||caldavCalStatus==="loading"||caldavCalStatus==="error")&&(()=>{const cdCollapsed=!expandedProviders.has("caldav");return <div style={{background:T.surface,borderRadius:14,marginBottom:10,border:`1px solid ${caldavCalStatus==="connected"?"#5A7BAE40":caldavCalStatus==="error"?T.red+"40":T.edge}`,overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",cursor:caldavCalStatus==="connected"?"pointer":"default"}} onClick={()=>caldavCalStatus==="connected"&&toggleCollapse("caldav")}>
          <span style={{fontSize:22,flexShrink:0}}>📅</span>
          <div style={{flex:1,minWidth:0}}>
            <p style={{fontSize:14,fontWeight:600,color:T.ink}}>Other CalDAV</p>
            {caldavCalStatus==="connected"&&<p style={{fontSize:11,color:"#5A7BAE",marginTop:2}}>Connected · {fmtSynced(syncedAt.caldav)}{caldavCalendars?.length>0&&<span style={{color:T.muted}}> · {caldavSelectedCalIds?.length||0}/{caldavCalendars.length} calendars</span>}</p>}
            {caldavCalStatus==="loading"&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Connecting…</p>}
            {caldavCalStatus==="error"&&<p style={{fontSize:11,color:T.red,marginTop:2}}>Connection error</p>}
            {(caldavCalStatus===null||!caldavCalStatus)&&<p style={{fontSize:11,color:T.muted,marginTop:2}}>Not connected · Fastmail, Nextcloud, Zoho &amp; more</p>}
          </div>
          {caldavCalStatus==="connected"&&<span style={{fontSize:16,color:T.bark,transition:"transform .2s",display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:6,background:T.mist,border:`1px solid ${T.edge}`,transform:cdCollapsed?"rotate(0deg)":"rotate(90deg)",marginRight:8,flexShrink:0}}>›</span>}
          {caldavCalStatus==="connected"&&(
            <button onClick={async(e)=>{e.stopPropagation();
              try{
                const {data:{session}}=await supabase.auth.getSession();
                if(!session) return;
                const res=await apiFetch("/api/caldav/disconnect",{method:"DELETE",headers:{Authorization:`Bearer ${session.access_token}`}});
                if(!res.ok) return;
                setCaldavCalStatus(null);
                setCaldavCalendars(null);
                setCaldavSelectedCalIds(null);
                setCalEvents(prev=>prev.filter(e=>e.source!=="caldav"));
              }catch(_){}
            }} style={{fontSize:11,color:T.red,background:"none",border:"none",cursor:"pointer",padding:0,flexShrink:0}}>Disconnect</button>
          )}
          {(caldavCalStatus===null||!caldavCalStatus||caldavCalStatus==="error")&&(
            <button onClick={(e)=>{e.stopPropagation();setChosenProvider(CAL_PROVIDERS.find(p=>p.id==="caldav"));setFormEmail("");setFormPass("");setFormServerUrl("");setFormError("");setFlow("form");}} style={{fontSize:11,color:"#5A7BAE",background:"none",border:"none",cursor:"pointer",padding:0,fontWeight:600,flexShrink:0}}>Connect</button>
          )}
          {caldavCalStatus==="loading"&&(
            <div style={{width:7,height:7,borderRadius:"50%",background:T.muted,animation:"pulse 1.5s infinite",flexShrink:0}}/>
          )}
        </div>
        {caldavCalStatus==="connected"&&!cdCollapsed&&(
          <div style={{borderTop:`1px solid ${T.edge}`,background:T.panel}}>
            {caldavListLoading&&<p style={{fontSize:11,color:T.muted,padding:"10px 16px"}}>Loading calendars…</p>}
            {!caldavListLoading&&caldavCalendars&&caldavCalendars.map((cal,i)=>{
              const sel=caldavSelectedCalIds?.includes(cal.id)??true;
              const calName=calendarCustomNames[cal.id]||cal.name;
              return(<div key={cal.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",borderBottom:i<caldavCalendars.length-1?`1px solid ${T.edge}`:"none"}}>
                <div style={{width:12,height:12,borderRadius:3,background:cal.color||"#5A7BAE",flexShrink:0}}/>
                {editingCalId===cal.id
                  ?<input autoFocus value={editingCalName} onChange={e=>setEditingCalName(e.target.value)} onBlur={()=>{setCalendarCustomNames(n=>({...n,[cal.id]:editingCalName}));setEditingCalId(null);}} onKeyDown={e=>{if(e.key==="Enter"){setCalendarCustomNames(n=>({...n,[cal.id]:editingCalName}));setEditingCalId(null);}if(e.key==="Escape")setEditingCalId(null);}} style={{flex:1,fontSize:13,color:T.ink,background:"none",border:"none",borderBottom:`1px solid ${T.clay}`,outline:"none",padding:"0 2px"}}/>
                  :<span onClick={()=>{setEditingCalId(cal.id);setEditingCalName(calName);}} style={{flex:1,fontSize:13,color:T.ink,cursor:"text"}} title="Click to rename">{calName}</span>}
                <div onClick={()=>toggleCaldavCal(cal.id)} style={{width:42,height:24,borderRadius:12,background:sel?"#5A7BAE":T.edge,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:sel?21:3,width:18,height:18,borderRadius:"50%",background:"white",boxShadow:"0 1px 3px rgba(0,0,0,.25)",transition:"left .2s"}}/>
                </div>
              </div>);
            })}
          </div>
        )}
      </div>;})()}

      {/* Account list (other providers — mock) */}
      {accounts.map(acc=>{
        const prov=CAL_PROVIDERS.find(p=>p.id===acc.providerId)||{icon:"📅",color:T.muted};
        const isExpanded=expandedAcc===acc.id;
        const enabledCount=acc.calendars.filter(c=>c.enabled).length;
        return(<div key={acc.id} style={{background:T.surface,borderRadius:14,marginBottom:10,border:`1px solid ${T.edge}`,overflow:"hidden"}}>
          {/* Account row */}
          <div onClick={()=>setExpandedAcc(isExpanded?null:acc.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",cursor:"pointer"}}>
            <span style={{fontSize:22,flexShrink:0}}>{prov.icon}</span>
            <div style={{flex:1,minWidth:0}}>
              <p style={{fontSize:14,fontWeight:600,color:T.ink}}>{acc.label}</p>
              <p style={{fontSize:11,color:T.muted}}>{acc.email!=="ofw-linked"?acc.email:""}{acc.email!=="ofw-linked"?" · ":""}{enabledCount} of {acc.calendars.length} calendars enabled</p>
            </div>
            <span style={{fontSize:16,color:T.bark,transition:"transform .2s",display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:6,background:T.mist,border:`1px solid ${T.edge}`,transform:isExpanded?"rotate(90deg)":"rotate(0deg)"}}>›</span>
          </div>
          {/* Calendar list */}
          {isExpanded&&<div style={{borderTop:`1px solid ${T.edge}`,background:T.panel}}>
            {acc.calendars.map((cal,i)=>(
              <div key={cal.id} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 16px",borderBottom:i<acc.calendars.length-1?`1px solid ${T.edge}`:"none"}}>
                <div style={{width:12,height:12,borderRadius:3,background:cal.color,flexShrink:0}}/>
                <span style={{flex:1,fontSize:13,color:T.ink}}>{cal.name}</span>
                {/* iOS-style toggle */}
                <div onClick={(e)=>{e.stopPropagation();toggleCal(acc.id,cal.id);}} style={{width:42,height:24,borderRadius:12,background:cal.enabled?T.sage:T.edge,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                  <div style={{position:"absolute",top:3,left:cal.enabled?21:3,width:18,height:18,borderRadius:"50%",background:"white",boxShadow:"0 1px 3px rgba(0,0,0,.25)",transition:"left .2s"}}/>
                </div>
              </div>
            ))}
            <div style={{padding:"10px 16px",borderTop:`1px solid ${T.edge}`}}>
              <button onClick={()=>removeAccount(acc.id)} style={{fontSize:11,color:T.red,background:"none",border:"none",cursor:"pointer",padding:0}}>Remove account</button>
            </div>
          </div>}
        </div>);
      })}

      {/* Add Account button */}
      <button onClick={startAdd} style={{width:"100%",padding:"13px",borderRadius:14,background:T.surface,border:`1.5px dashed ${T.edge}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontSize:13,color:T.clay,fontWeight:600,transition:"all .15s"}}>
        <span style={{fontSize:18}}>➕</span> Add Account
      </button>
    </>}

    {/* Conflicts tab */}
    {tab==="conflicts"&&<div>
      {openConflicts.length===0&&<div style={{textAlign:"center",padding:"32px 24px"}}><p style={{fontSize:26,marginBottom:8}}>✓</p><p style={{fontSize:14,fontFamily:"'Crimson Pro',serif",fontWeight:300}}>No conflicts</p></div>}
      {openConflicts.map(c=>{
        const fmtTime=(s,e)=>{
          const fmt=h=>{const hh=Math.floor(h);const mm=Math.round((h-hh)*60);const ampm=hh<12?"AM":"PM";const hr=hh%12||12;return mm?`${hr}:${String(mm).padStart(2,"0")} ${ampm}`:`${hr} ${ampm}`;};
          return`${fmt(s)}–${fmt(e)}`;
        };
        const orbitDesc=`${c.orbitEvent.label} · ${fmtTime(c.orbitEvent.startH,c.orbitEvent.endH)}`;
        const calDesc=`${c.calEvent.label} · ${fmtTime(c.calEvent.startH,c.calEvent.endH)}`;
        const calSource=c.calEvent.source;
        const calIcon=calSource==="google"?"🗓️":calSource==="icloud"?"☁️":calSource==="outlook"?"📆":calSource==="yahoo"?"💜":calSource==="ofw"?"📋":"📅";
        return(
        <div key={c.id} style={{background:T.surface,borderRadius:14,border:`1.5px solid ${c.sev==="high"?T.red+"40":T.amber+"40"}`,padding:16,marginBottom:10}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
            <span style={{fontSize:11,fontWeight:700,color:c.sev==="high"?T.red:T.amber,background:(c.sev==="high"?T.red:T.amber)+"18",padding:"2px 8px",borderRadius:10,textTransform:"uppercase"}}>{c.sev}</span>
            <p style={{fontSize:13,fontWeight:600,color:T.ink}}>{c.orbitEvent.date}</p>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
            <div style={{padding:"7px 10px",borderRadius:9,background:T.partner+"10",border:`1px solid ${T.partner}25`,fontSize:12,color:T.bark}}>◎ Orbit: {orbitDesc}</div>
            <div style={{padding:"7px 10px",borderRadius:9,background:T.mist,border:`1px solid ${T.edge}`,fontSize:12,color:T.bark}}>{calIcon} {calDesc}</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setResolved(r=>[...r,c.id])} style={{width:"100%",padding:8,borderRadius:9,background:T.sage,border:"none",color:"white",fontSize:11,fontWeight:600,cursor:"pointer"}}>Mark resolved</button>
          </div>
        </div>
        );
      })}
    </div>}

    {/* ── Flow modals ── */}
    {flow&&<div onClick={()=>setFlow(null)} style={{position:isMobile?"absolute":"fixed",inset:0,background:"rgba(30,16,8,0.45)",zIndex:600,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:480,background:T.surface,borderRadius:"20px 20px 0 0",padding:"0 0 32px",animation:"fadeUp .2s ease",maxHeight:"85vh",overflowY:"auto"}}>

        {/* Drag handle */}
        <div style={{width:36,height:4,borderRadius:2,background:T.edge,margin:"12px auto 20px"}}/>

        {/* Provider picker */}
        {flow==="picker"&&<>
          <p style={{fontSize:17,fontWeight:600,color:T.ink,textAlign:"center",marginBottom:20,paddingTop:4}}>Add Account</p>
          <div style={{padding:"0 16px",display:"flex",flexDirection:"column",gap:0,borderRadius:14,overflow:"hidden",border:`1px solid ${T.edge}`,margin:"0 20px"}}>
            {CAL_PROVIDERS.filter(p=>
              // OFW: only one import at a time
              !(p.id==="ofw"&&ofwCalStatus==="connected")&&
              // Other CalDAV: hide once one account is connected
              !(p.id==="caldav"&&(caldavCalStatus==="connected"||accounts.some(a=>a.providerId==="caldav")))
            ).map((p,i,arr)=>(
              <div key={p.id} onClick={()=>pickProvider(p)} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 16px",borderBottom:i<arr.length-1?`1px solid ${T.edge}`:"none",cursor:"pointer",background:T.surface,transition:"background .12s"}}
                onMouseEnter={e=>e.currentTarget.style.background=T.mist}
                onMouseLeave={e=>e.currentTarget.style.background=T.surface}>
                <span style={{fontSize:22,width:32,textAlign:"center",flexShrink:0}}>{p.icon}</span>
                <span style={{fontSize:15,color:p.id==="caldav"?T.clay:T.ink,fontWeight:p.id==="caldav"?600:400,flex:1}}>{p.label}</span>
                {p.id!=="caldav"&&<span style={{fontSize:13,color:T.muted}}>›</span>}
              </div>
            ))}
          </div>
          <button onClick={()=>setFlow(null)} style={{display:"block",margin:"20px auto 0",padding:"8px 24px",borderRadius:20,background:T.mist,border:`1px solid ${T.edge}`,fontSize:13,color:T.muted,cursor:"pointer"}}>Cancel</button>
        </>}

        {/* Sign-in form */}
        {flow==="form"&&chosenProvider&&<div style={{padding:"0 24px"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:24,justifyContent:"center",flexDirection:"column"}}>
            <span style={{fontSize:36}}>{chosenProvider.icon}</span>
            <p style={{fontSize:17,fontWeight:600,color:T.ink}}>{chosenProvider.label}</p>
          </div>
          {chosenProvider.id==="ofw"?(
            <>
              <div style={{background:T.coparent+"12",border:`1px solid ${T.coparent}30`,borderRadius:10,padding:"10px 12px",marginBottom:12}}>
                <p style={{fontSize:12,fontWeight:600,color:T.coparent,marginBottom:4}}>How to export your OFW calendar</p>
                <p style={{fontSize:11,color:T.mid,lineHeight:1.8}}>
                  1. Sign in to <strong>ourfamilywizard.com</strong> → Calendar<br/>
                  2. Click the export icon → <strong>Export (.ics)</strong><br/>
                  3. Choose a date range, click <strong>Export (.ics)</strong><br/>
                  4. Select the saved file below
                </p>
              </div>
              <label style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,padding:"20px 16px",borderRadius:10,border:`2px dashed ${ofwFileContent?T.coparent:T.edge}`,background:ofwFileContent?T.coparent+"08":T.mist,cursor:"pointer",marginBottom:12,transition:"all .15s"}}>
                <span style={{fontSize:22}}>{ofwFileContent?"✅":"📂"}</span>
                <span style={{fontSize:13,fontWeight:600,color:ofwFileContent?T.coparent:T.ink}}>
                  {ofwFileContent?"File ready — click to replace":"Choose .ics file"}
                </span>
                <input type="file" accept=".ics,text/calendar" style={{display:"none"}}
                  onChange={e=>{
                    const file=e.target.files?.[0];
                    if(!file) return;
                    const reader=new FileReader();
                    reader.onload=ev=>setOfwFileContent(ev.target?.result||"");
                    reader.readAsText(file);
                  }}/>
              </label>
            </>
          ):(
            <>
              {chosenProvider.id==="caldav"&&(
                <input value={formServerUrl} onChange={e=>setFormServerUrl(e.target.value)}
                  placeholder="CalDAV server URL (e.g. https://caldav.fastmail.com)"
                  type="url" style={inputStyle}/>
              )}
              <input value={formEmail} onChange={e=>setFormEmail(e.target.value)}
                placeholder={chosenProvider.id==="icloud"?"Apple ID (email)":chosenProvider.id==="yahoo"?"Yahoo email address":chosenProvider.domain?`Email (@${chosenProvider.domain})`:"Username or email"}
                type={chosenProvider.id==="caldav"?"text":"email"} style={inputStyle}/>
              <input value={formPass} onChange={e=>setFormPass(e.target.value)}
                placeholder={chosenProvider.id==="icloud"||chosenProvider.id==="yahoo"?"App-specific password":"Password"}
                type="password" style={inputStyle}/>
              {chosenProvider.id==="icloud"?(
                <div style={{background:"#1C7BE012",border:"1px solid #1C7BE030",borderRadius:10,padding:"10px 12px",marginBottom:14}}>
                  <p style={{fontSize:12,fontWeight:600,color:"#1C7BE0",marginBottom:4}}>App-specific password required</p>
                  <p style={{fontSize:11,color:T.mid,lineHeight:1.6}}>
                    Apple requires <strong>Two-Factor Authentication</strong> to be enabled on your Apple ID, then you can generate an app-specific password:
                  </p>
                  <p style={{fontSize:11,color:T.mid,lineHeight:1.8,marginTop:4}}>
                    1. Go to <strong>appleid.apple.com</strong><br/>
                    2. Sign-In and Security → App-Specific Passwords<br/>
                    3. Click + and name it "Orbit"<br/>
                    4. Copy the generated password here
                  </p>
                </div>
              ):chosenProvider.id==="yahoo"?(
                <div style={{background:"#6001D212",border:"1px solid #6001D230",borderRadius:10,padding:"10px 12px",marginBottom:14}}>
                  <p style={{fontSize:12,fontWeight:600,color:"#6001D2",marginBottom:4}}>App-specific password required</p>
                  <p style={{fontSize:11,color:T.mid,lineHeight:1.8}}>
                    1. Go to <strong>login.yahoo.com</strong><br/>
                    2. Account Security → Generate app password<br/>
                    3. Select "Other App", name it "Orbit"<br/>
                    4. Copy the generated password here
                  </p>
                </div>
              ):(
                <p style={{fontSize:11,color:T.muted,textAlign:"center",marginBottom:16,lineHeight:1.5}}>
                  Orbit will access your calendar events to show availability.
                </p>
              )}
            </>
          )}
          {formError&&<p style={{fontSize:12,color:T.red,background:T.red+"12",padding:"9px 12px",borderRadius:10,border:`1px solid ${T.red}30`,marginBottom:12,lineHeight:1.5}}>{formError}</p>}
          <button onClick={submitForm} style={{width:"100%",padding:"13px",borderRadius:12,background:chosenProvider.color,border:"none",color:"white",fontSize:14,fontWeight:600,cursor:"pointer",marginBottom:10,opacity:(chosenProvider.id==="ofw"?ofwFileContent.trim():formEmail.trim())?1:0.5}}>
            {chosenProvider.id==="ofw"&&ofwCalStatus==="connected"?"Re-import":"Connect"}
          </button>
          <button onClick={()=>{
            // Re-import: OFW is filtered from the picker when connected, so
            // Back should close the sheet rather than navigate to a broken picker state.
            if(ofwCalStatus==="connected"&&chosenProvider?.id==="ofw"){setFlow(null);}
            else{setFlow("picker");setFormError("");}
          }} style={{width:"100%",padding:"10px",borderRadius:12,background:T.mist,border:`1px solid ${T.edge}`,fontSize:13,color:T.muted,cursor:"pointer"}}>
            ← Back
          </button>
        </div>}

        {/* Connecting */}
        {flow==="connecting"&&<div style={{padding:"40px 24px",textAlign:"center"}}>
          <div style={{width:56,height:56,borderRadius:"50%",background:chosenProvider?.color+"18",border:`2px solid ${chosenProvider?.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,margin:"0 auto 16px",animation:"pulse 1s infinite"}}>{chosenProvider?.icon}</div>
          <p style={{fontSize:16,fontWeight:600,color:T.ink,marginBottom:6}}>Connecting…</p>
          <p style={{fontSize:13,color:T.muted}}>Signing in to {chosenProvider?.label}</p>
        </div>}

        {/* Done */}
        {flow==="done"&&<div style={{padding:"40px 24px",textAlign:"center"}}>
          <div style={{width:56,height:56,borderRadius:"50%",background:T.sage+"18",border:`2px solid ${T.sage}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,margin:"0 auto 16px"}}>✓</div>
          <p style={{fontSize:16,fontWeight:600,color:T.ink,marginBottom:6}}>Account added!</p>
          <p style={{fontSize:13,color:T.muted}}>{chosenProvider?.label} calendars are ready.</p>
        </div>}
      </div>
    </div>}
  </div>);
}

// ── Work Schedule Section ────────────────────────────────────────────────────
const WS_DAYS=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const WS_DAY_FULL=["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

function WorkScheduleSection(){
  const {workSchedules,workScheduleExceptions,saveWorkSchedule,deleteWorkSchedule,saveWorkException,deleteWorkException}=usePeople();
  const [editing,setEditing]=useState(null); // null = list view, {id?,label,enabled,blocks} = editor
  const [addingException,setAddingException]=useState(null); // schedule_id being edited
  const [excLabel,setExcLabel]=useState("Vacation");
  const [excFrom,setExcFrom]=useState("");
  const [excTo,setExcTo]=useState("");
  const [saving,setSaving]=useState(false);
  const [deleteConfirm,setDeleteConfirm]=useState(null);

  const startNew=()=>setEditing({label:"Work",enabled:true,blocks:[
    {day:1,startH:9,endH:17},{day:2,startH:9,endH:17},{day:3,startH:9,endH:17},
    {day:4,startH:9,endH:17},{day:5,startH:9,endH:17},
  ]});
  const startEdit=(sched)=>setEditing({id:sched.id,label:sched.label,enabled:sched.enabled,blocks:[...(sched.blocks||[])]});

  const toggleDay=(day)=>{
    setEditing(prev=>{
      const has=prev.blocks.some(b=>b.day===day);
      if(has) return {...prev,blocks:prev.blocks.filter(b=>b.day!==day)};
      return {...prev,blocks:[...prev.blocks,{day,startH:9,endH:17}].sort((a,b)=>a.day-b.day)};
    });
  };
  const updateBlock=(day,field,val)=>{
    setEditing(prev=>({...prev,blocks:prev.blocks.map(b=>b.day===day?{...b,[field]:val}:b)}));
  };

  const handleSave=async()=>{
    setSaving(true);
    await saveWorkSchedule(editing);
    setSaving(false);
    setEditing(null);
  };

  const handleDelete=async(id)=>{
    await deleteWorkSchedule(id);
    setDeleteConfirm(null);
  };

  const handleToggleEnabled=async(sched)=>{
    await saveWorkSchedule({id:sched.id,label:sched.label,enabled:!sched.enabled,blocks:sched.blocks});
  };

  const handleAddException=async()=>{
    if(!addingException||!excFrom||!excTo) return;
    setSaving(true);
    await saveWorkException({schedule_id:addingException,label:excLabel,from_date:excFrom,to_date:excTo});
    setSaving(false);
    setAddingException(null);
    setExcLabel("Vacation");
    setExcFrom("");
    setExcTo("");
  };

  const fmtH=(h)=>{const hr=Math.floor(h);const ampm=hr>=12?"PM":"AM";const h12=hr===0?12:hr>12?hr-12:hr;return`${h12}:00 ${ampm}`;};

  // Editor view
  if(editing){
    return(<div>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
        <button onClick={()=>setEditing(null)} style={{width:28,height:28,borderRadius:"50%",background:T.mist,border:"none",cursor:"pointer",color:T.muted,fontSize:14}}>←</button>
        <h3 style={{fontSize:16,fontFamily:"'Crimson Pro',serif",fontWeight:600,color:T.ink}}>{editing.id?"Edit work schedule":"New work schedule"}</h3>
      </div>

      <Card>
        <div style={{padding:"14px 16px"}}>
          <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:6}}>Schedule name</label>
          <input value={editing.label} onChange={e=>setEditing(p=>({...p,label:e.target.value}))} style={{width:"100%",padding:"8px 12px",borderRadius:9,border:`1.5px solid ${T.edge}`,background:T.panel,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box"}}/>
        </div>

        <div style={{padding:"0 16px 14px"}}>
          <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:8}}>Work days</label>
          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:14}}>
            {WS_DAYS.map((d,i)=>{
              const active=editing.blocks.some(b=>b.day===i);
              return(<button key={i} onClick={()=>toggleDay(i)} style={{width:40,height:40,borderRadius:10,border:`1.5px solid ${active?T.clay:T.edge}`,background:active?T.clay+"15":"transparent",color:active?T.clay:T.muted,fontSize:11,fontWeight:active?700:400,cursor:"pointer",transition:"all .12s"}}>{d}</button>);
            })}
          </div>

          {editing.blocks.sort((a,b)=>a.day-b.day).map(block=>(
            <div key={block.day} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,padding:"8px 12px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`}}>
              <span style={{fontSize:12,fontWeight:600,color:T.ink,width:40}}>{WS_DAYS[block.day]}</span>
              <select value={block.startH} onChange={e=>updateBlock(block.day,"startH",+e.target.value)} style={{flex:1,padding:"5px 8px",borderRadius:8,border:`1px solid ${T.edge}`,background:T.panel,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                {Array.from({length:24},(_,h)=><option key={h} value={h}>{fmtH(h)}</option>)}
              </select>
              <span style={{fontSize:11,color:T.muted}}>to</span>
              <select value={block.endH} onChange={e=>updateBlock(block.day,"endH",+e.target.value)} style={{flex:1,padding:"5px 8px",borderRadius:8,border:`1px solid ${T.edge}`,background:T.panel,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                {Array.from({length:24},(_,h)=>h+1).filter(h=>h>block.startH).map(h=><option key={h} value={h}>{fmtH(h)}</option>)}
              </select>
            </div>
          ))}
        </div>
      </Card>

      <div style={{display:"flex",gap:8,marginTop:12}}>
        <button onClick={()=>setEditing(null)} style={{flex:1,padding:"10px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,color:T.bark,fontSize:13,fontWeight:500,cursor:"pointer"}}>Cancel</button>
        <button onClick={handleSave} disabled={saving||editing.blocks.length===0} style={{flex:1,padding:"10px",borderRadius:10,background:saving?T.mist:`linear-gradient(135deg,${T.bark},${T.clay})`,border:"none",color:saving?T.muted:"white",fontSize:13,fontWeight:600,cursor:saving?"not-allowed":"pointer",opacity:editing.blocks.length===0?0.5:1}}>
          {saving?"Saving...":editing.id?"Save changes":"Create schedule"}
        </button>
      </div>
    </div>);
  }

  // List view
  return(<div>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
      <div>
        <h3 style={{fontSize:18,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink}}>Work Schedule</h3>
        <p style={{fontSize:12,color:T.muted,marginTop:2}}>Set your regular working hours to block availability</p>
      </div>
      <button onClick={startNew} style={{padding:"7px 14px",borderRadius:10,background:`linear-gradient(135deg,${T.bark},${T.clay})`,border:"none",color:"white",fontSize:12,fontWeight:600,cursor:"pointer"}}>+ New schedule</button>
    </div>

    {workSchedules.length===0&&(
      <Card>
        <div style={{padding:"32px 20px",textAlign:"center"}}>
          <div style={{fontSize:36,marginBottom:12}}>💼</div>
          <p style={{fontSize:14,fontWeight:600,color:T.bark,marginBottom:6}}>No work schedule set</p>
          <p style={{fontSize:12,color:T.muted,lineHeight:1.5,marginBottom:16}}>Add your working hours so they show as unavailable when scheduling hangouts. Work blocks don't fill your calendar — they just mark you as busy during those hours.</p>
          <button onClick={startNew} style={{padding:"9px 18px",borderRadius:10,background:T.clay,border:"none",color:"white",fontSize:13,fontWeight:600,cursor:"pointer"}}>Set up work hours</button>
        </div>
      </Card>
    )}

    {workSchedules.map(sched=>{
      const excs=(workScheduleExceptions||[]).filter(e=>e.schedule_id===sched.id);
      const dayStr=(sched.blocks||[]).sort((a,b)=>a.day-b.day).map(b=>WS_DAYS[b.day]).join(", ");
      const timeStr=(sched.blocks||[]).length>0?`${fmtH(sched.blocks[0].startH)} – ${fmtH(sched.blocks[0].endH)}`:"";
      const allSameTime=(sched.blocks||[]).every(b=>b.startH===sched.blocks[0]?.startH&&b.endH===sched.blocks[0]?.endH);

      return(<Card key={sched.id}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderBottom:`1px solid ${T.edge}`}}>
          <div style={{width:36,height:36,borderRadius:10,background:sched.enabled?T.clay+"18":T.mist,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>💼</div>
          <div style={{flex:1,minWidth:0}}>
            <p style={{fontSize:14,fontWeight:600,color:sched.enabled?T.ink:T.muted}}>{sched.label}</p>
            <p style={{fontSize:11,color:T.muted,marginTop:1}}>
              {dayStr}{allSameTime&&timeStr?` · ${timeStr}`:""}
            </p>
          </div>
          <Tog on={sched.enabled} set={()=>handleToggleEnabled(sched)} c={T.clay}/>
        </div>

        {/* Individual day blocks if times differ */}
        {!allSameTime&&(sched.blocks||[]).sort((a,b)=>a.day-b.day).map(b=>(
          <div key={b.day} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 16px 6px 64px",borderBottom:`1px solid ${T.edge}`,fontSize:12,color:T.bark}}>
            <span style={{width:40,fontWeight:600}}>{WS_DAYS[b.day]}</span>
            <span style={{color:T.muted}}>{fmtH(b.startH)} – {fmtH(b.endH)}</span>
          </div>
        ))}

        {/* Exceptions (vacations) */}
        {excs.length>0&&(
          <div style={{padding:"8px 16px",borderBottom:`1px solid ${T.edge}`}}>
            <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Exceptions</p>
            {excs.map(ex=>(
              <div key={ex.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",fontSize:12}}>
                <span style={{color:T.partner,fontSize:11}}>🏖️</span>
                <span style={{fontWeight:500,color:T.ink}}>{ex.label}</span>
                <span style={{color:T.muted,fontSize:11}}>{ex.from_date} → {ex.to_date}</span>
                <button onClick={()=>deleteWorkException(ex.id)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:T.red,fontSize:11,padding:"2px 6px"}}>Remove</button>
              </div>
            ))}
          </div>
        )}

        {/* Add exception form */}
        {addingException===sched.id&&(
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${T.edge}`,background:T.mist}}>
            <p style={{fontSize:12,fontWeight:600,color:T.ink,marginBottom:8}}>Add time off</p>
            <input value={excLabel} onChange={e=>setExcLabel(e.target.value)} placeholder="Label (e.g. Vacation, PTO)" style={{width:"100%",padding:"7px 10px",borderRadius:8,border:`1.5px solid ${T.edge}`,background:T.panel,fontSize:12,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box",marginBottom:8}}/>
            <div style={{display:"flex",gap:8,marginBottom:8}}>
              <div style={{flex:1}}>
                <label style={{fontSize:11,color:T.muted,display:"block",marginBottom:3}}>From</label>
                <input type="date" value={excFrom} onChange={e=>setExcFrom(e.target.value)} style={{width:"100%",padding:"6px 8px",borderRadius:8,border:`1.5px solid ${T.edge}`,background:T.panel,fontSize:12,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{flex:1}}>
                <label style={{fontSize:11,color:T.muted,display:"block",marginBottom:3}}>To</label>
                <input type="date" value={excTo} onChange={e=>setExcTo(e.target.value)} style={{width:"100%",padding:"6px 8px",borderRadius:8,border:`1.5px solid ${T.edge}`,background:T.panel,fontSize:12,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box"}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>setAddingException(null)} style={{flex:1,padding:"7px",borderRadius:8,background:T.surface,border:`1px solid ${T.edge}`,color:T.bark,fontSize:12,cursor:"pointer"}}>Cancel</button>
              <button onClick={handleAddException} disabled={!excFrom||!excTo||saving} style={{flex:1,padding:"7px",borderRadius:8,background:T.clay,border:"none",color:"white",fontSize:12,fontWeight:600,cursor:"pointer",opacity:(!excFrom||!excTo)?0.5:1}}>{saving?"Saving...":"Add"}</button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{display:"flex",gap:8,padding:"10px 16px"}}>
          <button onClick={()=>startEdit(sched)} style={{padding:"5px 12px",borderRadius:8,background:T.mist,border:`1px solid ${T.edge}`,color:T.bark,fontSize:11,cursor:"pointer",fontWeight:500}}>Edit</button>
          <button onClick={()=>{setAddingException(sched.id);setExcLabel("Vacation");setExcFrom("");setExcTo("");}} style={{padding:"5px 12px",borderRadius:8,background:T.mist,border:`1px solid ${T.edge}`,color:T.bark,fontSize:11,cursor:"pointer",fontWeight:500}}>+ Time off</button>
          {deleteConfirm===sched.id
            ?<div style={{display:"flex",gap:4,marginLeft:"auto"}}>
              <button onClick={()=>setDeleteConfirm(null)} style={{padding:"5px 10px",borderRadius:8,background:T.mist,border:`1px solid ${T.edge}`,color:T.bark,fontSize:11,cursor:"pointer"}}>Cancel</button>
              <button onClick={()=>handleDelete(sched.id)} style={{padding:"5px 10px",borderRadius:8,background:T.red,border:"none",color:"white",fontSize:11,fontWeight:600,cursor:"pointer"}}>Confirm delete</button>
            </div>
            :<button onClick={()=>setDeleteConfirm(sched.id)} style={{padding:"5px 12px",borderRadius:8,background:"transparent",border:`1px solid ${T.red}30`,color:T.red,fontSize:11,cursor:"pointer",marginLeft:"auto"}}>Delete</button>
          }
        </div>
      </Card>);
    })}
  </div>);
}

